import {
  AmbientLight,
  AnimationMixer,
  AxesHelper,
  Box3,
  Cache,
  CineonToneMapping,
  CubeCamera,
  DirectionalLight,
  EquirectangularReflectionMapping,
  GridHelper,
  Group,
  HemisphereLight,
  LinearEncoding,
  LinearMipmapLinearFilter,
  LoaderUtils,
  LoadingManager,
  NearestFilter,
  PMREMGenerator,
  PerspectiveCamera,
  PointLight,
  Raycaster,
  REVISION,
  RGBFormat,
  Scene,
  SkeletonHelper,
  sRGBEncoding,
  TextureLoader,
  UnsignedByteType,
  Vector2,
  Vector3,
  WebGLRenderer,
  WebGLCubeRenderTarget,
} from 'three';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';

import { CubemapToEquirectangular } from './CubemapToEquirectangular.js';

import { GUI } from 'dat.gui';

import { environments } from '../assets/environment/index.js';
import { createBackground } from '../lib/three-vignette.js';

import { Animator } from './animator.js';
import { ViewController } from './viewcontroller.js';

const DEFAULT_CAMERA = '[default]';

const MANAGER = new LoadingManager();
const THREE_PATH = `https://unpkg.com/three@0.${REVISION}.x`;
const DRACO_LOADER = new DRACOLoader(MANAGER).setDecoderPath(
  `${THREE_PATH}/examples/js/libs/draco/gltf/`
);
const KTX2_LOADER = new KTX2Loader(MANAGER).setTranscoderPath(
  `${THREE_PATH}/examples/js/libs/basis/`
);

const IS_IOS = isIOS();

// glTF texture types. `envMap` is deliberately omitted, as it's used internally
// by the loader but not part of the glTF format.
const MAP_NAMES = [
  'map',
  'aoMap',
  'emissiveMap',
  'glossinessMap',
  'metalnessMap',
  'normalMap',
  'roughnessMap',
  'specularMap',
];

const Preset = { ASSET_GENERATOR: 'assetgenerator' };

Cache.enabled = true;

export class Viewer {
  constructor(el, options) {
    window.updatePatterns = this.updatePatterns.bind(this);
    window.generateEnvMap = this.generateEnvMap.bind(this);
    window.saveImage = this.saveImage.bind(this);
    window.reset = this.reset.bind(this);
    window.resize = this.resize.bind(this);
    window.onControl = this.onControl.bind(this);
    window.updateFloorUI = this.updateFloorUI;

    this.jsonData = null;
    this.Presets = null;
    this.currentPause = true;
    this.envMap = null;
    this.disableIds = [];
    this.loadedModel = {
      name: null,
      type: null,
      component: null,
    };
    this.lightMapState = {
      needsUpdate: false,
      key: null,
      target_prefix: null,
      lightmap_targets: null,
      default_prefix: null,
    };
    this.currentPreset = 0;

    this.el = el;
    this.spinnerEl = document.querySelector('.loader');
    this.coverEl = document.querySelector('.cover');
    this.options = options;

    this.lights = [];
    this.content = null;
    this.mixer = null;
    this.clips = [];
    this.gui = null;

    this.state = {
      environment:
        options.preset === Preset.ASSET_GENERATOR
          ? environments.find((e) => e.id === 'footprint-court').name
          : environments[1].name,
      background: false,
      playbackSpeed: 1.0,
      actionStates: {},
      camera: DEFAULT_CAMERA,
      wireframe: false,
      skeleton: false,
      grid: false,
      pause: true,

      // Lights
      addLights: true,
      toneMapExposure: 1.6,
      textureEncoding: 'sRGB',
      ambientIntensity: 0,
      ambientColor: 0xffffff,
      directIntensity: 0.0 * Math.PI, // TODO(#116)
      directColor: 0xffffff,
      pointIntensity: 0.2,
      pointColor: 0xffffff,
      pointDistance: 1,
      pointDecay: 0.4,
      bgColor1: '#000000',
      bgColor2: '#000000',
      lightMapIntensity: 4.5,
      aoMapIntensity: 0.45,
    };

    this.prevTime = 0;

    this.stats = new Stats();
    this.stats.dom.height = '48px';
    [].forEach.call(
      this.stats.dom.children,
      (child) => (child.style.display = '')
    );

    this.scene = new Scene();

    const fov =
      options.preset === Preset.ASSET_GENERATOR ? (0.8 * 180) / Math.PI : 30;
    this.defaultCamera = new PerspectiveCamera(
      fov,
      el.clientWidth / el.clientHeight,
      0.01,
      1000
    );
    this.activeCamera = this.defaultCamera;
    this.scene.add(this.defaultCamera);

    // Create cube render target
    this.cubeRenderTarget = new WebGLCubeRenderTarget(64, {
      format: RGBFormat,
      generateMipmaps: true,
      minFilter: LinearMipmapLinearFilter,
      encoding: sRGBEncoding, // temporary -- to prevent the material's shader from recompiling every frame
    });

    // Create cube camera
    this.cubeCamera = new CubeCamera(0.01, 1000, this.cubeRenderTarget);
    this.scene.add(this.cubeCamera);

    this.renderer = window.renderer = new WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });
    this.renderer.toneMapping = CineonToneMapping;
    this.renderer.physicallyCorrectLights = true;
    this.renderer.outputEncoding = sRGBEncoding;
    this.renderer.setClearColor(0xcccccc);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(el.clientWidth, el.clientHeight);

    this.pmremGenerator = new PMREMGenerator(this.renderer);
    this.pmremGenerator.compileEquirectangularShader();

    this.controls = new OrbitControls(
      this.defaultCamera,
      this.renderer.domElement
    );
    this.controls.addEventListener('change', this.onControl.bind(this));
    this.controls.onControl = this.onControl.bind(this);
    this.controls.autoRotate = false;
    this.controls.autoRotateSpeed = -3;
    this.controls.screenSpacePanning = true;
    this.controls.enabled = false;

    this.raycaster = new Raycaster();
    this.raycaster.targets = [];

    this.vignette = createBackground({
      aspect: this.defaultCamera.aspect,
      grainScale: IS_IOS ? 0 : 0.001, // mattdesl/three-vignette-background#1
      colors: [this.state.bgColor1, this.state.bgColor2],
    });
    this.vignette.name = 'Vignette';
    this.vignette.renderOrder = -1;

    this.el.appendChild(this.renderer.domElement);

    this.cameraCtrl = null;
    this.cameraFolder = null;
    this.animFolder = null;
    this.animCtrls = [];
    this.morphFolder = null;
    this.morphCtrls = [];
    this.skeletonHelpers = [];
    this.gridHelper = null;
    this.axesHelper = null;

    this.addAxesHelper();
    this.addGUI();
    if (options.kiosk) this.gui.close();

    this.animate = this.animate.bind(this);
    requestAnimationFrame(this.animate);

    window.addEventListener(
      'hashchange',
      function () {
        if (this.location.hash != '') location.reload();
      },
      false
    );
    window.addEventListener('resize', this.resize.bind(this), false);
    window.addEventListener('mouseup', this.mouseCast.bind(this), false);
  }

  animate(time) {
    requestAnimationFrame(this.animate);

    if (!this.isLoaded) {
      this.progress();
    }

    if (this.state.pause) {
      this.currentPause = this.state.pause;
      this.controls.enabled = false;
      return;
    } else if (this.currentPause != this.state.pause) {
      this.currentPause = this.state.pause;
      this.controls.enabled = true;
      this.onControl();
    }

    if (this.loadedModel.component == 'Hall' && this.viewController) {
      const bb = new Box3().setFromObject(
        this.scene.getObjectByName('Collision')
      );
      if (bb.containsPoint(this.defaultCamera.position)) {
        this.controls.currentCameraPosition = JSON.parse(
          JSON.stringify(this.defaultCamera.position)
        );
        this.controls.currentControlTarget = JSON.parse(
          JSON.stringify(this.controls.target)
        );
      } else if (this.controls.currentCameraPosition) {
        this.defaultCamera.position.set(
          this.controls.currentCameraPosition.x,
          this.controls.currentCameraPosition.y,
          this.controls.currentCameraPosition.z
        );
        this.controls.target.set(
          this.controls.currentControlTarget.x,
          this.controls.currentControlTarget.y,
          this.controls.currentControlTarget.z
        );
        this.controls.update();
      }
    }

    const dt = (time - this.prevTime) / 1000;
    this.animateParam.indicatorTimer += dt;
    this.animateParam.indicatorIconTimer += dt;

    this.stats.update();
    this.mixer && this.mixer.update(dt);
    this.render();

    ////////////////////
    // Animations
    ////////////////////
    if (this.loadedModel.component == 'Cage') {
      this.animator.animateCage(dt);
    } else if (this.loadedModel.component == 'Hall') {
      this.animator.animateHall(dt);
    }

    this.prevTime = time;
  }

  render() {
    this.renderer.render(this.scene, this.activeCamera);
    if (this.state.grid) {
      this.axesCamera.position.copy(this.defaultCamera.position);
      this.axesCamera.lookAt(this.axesScene.position);
      this.axesRenderer.render(this.axesScene, this.axesCamera);
    }

    if (!this.initialRendered) {
      this.onControl();
      this.initialRendered = true;
    }
  }

  resize(event, h = null, w = null) {
    if (document.getElementById('UI').clientWidth < 30) {
      document.querySelector('.viewwrap').style.width = '';
      document.querySelector('.viewwrap').style.marginLeft = '';

      document.querySelector('.loader').style.width = '';
      document.querySelector('.loader').style.marginLeft = '';
    } else {
      document.querySelector('.viewwrap').style.width =
        window.innerWidth - document.getElementById('UI').clientWidth + 'px';
      document.querySelector('.viewwrap').style.marginLeft =
        document.getElementById('UI').clientWidth + 'px';

      document.querySelector('.loader').style.width =
        window.innerWidth - document.getElementById('UI').clientWidth + 'px';
      document.querySelector('.loader').style.marginLeft =
        document.getElementById('UI').clientWidth + 'px';
    }

    let { clientHeight, clientWidth } = this.el.parentElement;

    if (h) clientHeight = h;
    if (w) clientWidth = w;

    this.defaultCamera.aspect = clientWidth / clientHeight;
    this.defaultCamera.updateProjectionMatrix();
    this.vignette.style({ aspect: this.defaultCamera.aspect });
    this.renderer.setSize(clientWidth, clientHeight);

    this.axesCamera.aspect =
      this.axesDiv.clientWidth / this.axesDiv.clientHeight;
    this.axesCamera.updateProjectionMatrix();
    this.axesRenderer.setSize(
      this.axesDiv.clientWidth,
      this.axesDiv.clientHeight
    );

    calcWidth();
  }

  async load(url, rootPath, assetMap) {
    const baseURL = LoaderUtils.extractUrlBase(url);
    this.state.pause = true;
    this.disableIds = [];
    const UI = document.getElementById('custom_ui');
    while (UI.firstChild) {
      UI.removeChild(UI.firstChild);
    }

    // Load.
    return new Promise(async (resolve, reject) => {
      // Intercept and override relative URLs.
      MANAGER.setURLModifier((url, path) => {
        // URIs in a glTF file may be escaped, or not. Assume that assetMap is
        // from an un-escaped source, and decode all URIs before lookups.
        // See: https://github.com/donmccurdy/three-gltf-viewer/issues/146
        const normalizedURL =
          rootPath +
          decodeURI(url)
            .replace(baseURL, '')
            .replace(/^(\.?\/)/, '');

        if (assetMap.has(normalizedURL)) {
          const blob = assetMap.get(normalizedURL);
          const blobURL = URL.createObjectURL(blob);
          blobURLs.push(blobURL);
          return blobURL;
        }

        return (path || '') + url;
      });

      const loader = new GLTFLoader(MANAGER)
        .setCrossOrigin('anonymous')
        .setDRACOLoader(DRACO_LOADER)
        .setKTX2Loader(KTX2_LOADER.detectSupport(this.renderer))
        .setMeshoptDecoder(MeshoptDecoder);

      const blobURLs = [];

      // Store loaded model name
      this.loadedModel.name = url.split('/').reverse()[0].split('.')[0];
      this.loadedModel.type = this.loadedModel.name.split('_')[0];
      this.loadedModel.component = this.loadedModel.name.split('_')[1];
      console.debug(`[DEBUG] ${this.loadedModel.name}`);

      // Loader init
      document.querySelector('.loader').style.width =
        window.innerWidth - document.getElementById('UI').clientWidth + 'px';
      document.querySelector('.loader').style.marginLeft =
        document.getElementById('UI').clientWidth + 'px';

      // Remove sui ui on Halls
      if (this.loadedModel.component == 'Hall') {
        document.getElementById('sui_head').remove();
        document.getElementById('sui_ui').remove();

        // Camera Preset UI
        const camPresetUIs = Array.from(
          document.querySelectorAll('.button_container')[2].children
        );
        camPresetUIs.forEach((el, i) => {
          if (i == 3 || i == 5 || i == 6) el.remove();
          if (i == 4) el.src = 'assets/images/01_layout/lay_pre_btn7.png';
        });
      } else {
        // Build sui HTML
        const sui_ui = document.getElementById('sui_ui');

        let counter = 1;
        for (let i = 0; i < 2; i++) {
          // limitter init
          let max = 0;
          if (i == 0) {
            // CLEAN
            switch (this.loadedModel.type) {
              case 'P11': {
                max = 3;
                break;
              }
              case 'P24': {
                max = 2;
                break;
              }
              case 'R9': {
                max = 3;
                break;
              }
              case 'B750': {
                max = 2;
                break;
              }
              case 'EP26': {
                max = 4;
                break;
              }
            }
          } else {
            // CLASSIC
            switch (this.loadedModel.type) {
              case 'P11': {
                max = 4;
                break;
              }
              case 'P24': {
                max = 2;
                break;
              }
              case 'R9': {
                max = 3;
                break;
              }
              case 'B750': {
                max = 2;
                break;
              }
              case 'EP26': {
                max = 0;
                break;
              }
            }
          }

          const txImg = document.createElement('img');
          if (i == 0) txImg.src = 'assets/images/00_navigation/nav_sui_tx1.png';
          else if (max != 0)
            txImg.src = 'assets/images/00_navigation/nav_sui_tx2.png';
          if (txImg.src) sui_ui.appendChild(txImg);

          const sui_wrap = document.createElement('div');
          sui_wrap.classList.add('sui_wrap');

          for (let n = 0; n < max; n++) {
            const sui_div = document.createElement('div'),
              sui_ind = document.createElement('div'),
              sui_content = document.createElement('div');

            sui_div.style.position = 'relative';
            sui_div.style.display = 'inherit';

            sui_ind.style.position = 'absolute';
            sui_ind.style.width = sui_ind.style.height = '100%';
            sui_ind.style.pointerEvents = 'none';
            sui_ind.classList.add('sui_ind');

            sui_content.classList.add('sui_content');

            const suiImg = document.createElement('img');
            suiImg.src =
              'assets/images/00_navigation/nav_sui_' +
              this.loadedModel.type +
              '_btn' +
              counter +
              '.png';
            suiImg.setAttribute(
              'onclick',
              'updatePatterns(null, ' + counter + ')'
            );
            suiImg.style.width = suiImg.style.height = '100%';

            sui_div.appendChild(suiImg);
            sui_div.appendChild(sui_ind);
            sui_content.appendChild(sui_div);

            const suiImgTx = document.createElement('img');
            suiImgTx.src =
              'assets/images/00_navigation/nav_typ_tx' +
              this.loadedModel.type +
              '_' +
              counter +
              '.png';
            sui_content.appendChild(suiImgTx);

            sui_wrap.appendChild(sui_content);
            counter++;
          }

          sui_ui.appendChild(sui_wrap);
        }
      }

      this.progress(5);

      // Load Model
      let t0 = performance.now();
      const gltf = await loader.loadAsync(url);
      console.debug(
        '[DEBUG] Model loaded in %d ms: %s',
        (performance.now() - t0).toFixed(0),
        this.loadedModel.name
      );
      this.progress(20);

      const scene = gltf.scene || gltf.scenes[0];
      const clips = gltf.animations || [];

      // On cage models
      if (this.loadedModel.component == 'Cage') {
        scene.name = 'Cage';
      } else {
        scene.name = 'Hall';
      }

      // Activate UI
      switch (this.loadedModel.component) {
        case 'Cage': {
          document.querySelectorAll('.type_selector img').forEach((typeimg) => {
            typeimg.setAttribute(
              'onclick',
              typeimg.getAttribute('onclick')?.replace('_Hall', '_Cage') ?? null
            );
          });

          const compimgs = document.querySelectorAll('.comp_selector>img');
          compimgs[0].style.filter = 'invert(100%)';
          compimgs[1].setAttribute(
            'onclick',
            `window.location.hash = "model=${this.loadedModel.type}_Hall"`
          );
          compimgs[1].style.filter = '';
          break;
        }
        case 'Hall': {
          document.querySelectorAll('.type_selector img').forEach((typeimg) => {
            typeimg.setAttribute(
              'onclick',
              typeimg.getAttribute('onclick')?.replace('_Cage', '_Hall') ?? null
            );
          });

          const compimgs = document.querySelectorAll('.comp_selector>img');
          compimgs[0].style.filter = '';
          compimgs[0].setAttribute(
            'onclick',
            `window.location.hash = "model=${this.loadedModel.type}_Cage"`
          );
          compimgs[1].style.filter = 'invert(100%)';
          break;
        }
      }
      switch (this.loadedModel.type) {
        case 'P11':
        case 'P24': {
          const el = document.querySelectorAll('.type_selector>div>img')[0];
          el.src = 'assets/images/00_navigation/nav_typ_btn1_on.png';
          // if (this.loadedModel.type == 'P11') {
          //   el.nextElementSibling.firstElementChild.src =
          //     'assets/images/00_navigation/nav_typ_btn6_p11_on.png';
          //   el.nextElementSibling.firstElementChild.removeAttribute('onclick');
          // } else {
          //   el.nextElementSibling.lastElementChild.src =
          //     'assets/images/00_navigation/nav_typ_btn6_p24_on.png';
          //   el.nextElementSibling.lastElementChild.removeAttribute('onclick');
          // }
          break;
        }
        case 'R9': {
          const el = document.querySelectorAll('.type_selector>div>img')[1];
          el.src = 'assets/images/00_navigation/nav_typ_btn2_on.png';
          el.removeAttribute('onclick');
          break;
        }
        case 'B750': {
          const el = document.querySelectorAll('.type_selector>div>img')[2];
          el.src = 'assets/images/00_navigation/nav_typ_btn3_on.png';
          el.removeAttribute('onclick');
          break;
        }
        case 'EP26': {
          const el = document.querySelectorAll('.type_selector>div>img')[3];
          el.src = 'assets/images/00_navigation/nav_typ_btn6_on.png';
          el.removeAttribute('onclick');
          break;
        }
      }

      if (!scene) {
        // Valid, but not supported by this viewer.
        throw new Error(
          'This model contains no scene, and cannot be viewed here. However,' +
          ' it may contain individual 3D resources.'
        );
      }

      // Load Preset
      const presetRes = await fetch('assets/models/Presets.json');
      this.Presets = await presetRes.json();

      // Load default envMap
      t0 = performance.now();
      const environment = environments.filter(
        (entry) =>
          entry.name === this.loadedModel.name ||
          entry.name === 'photo_studio_01_4k'
      )[0];
      const mapTex = await this.getCubeMapTexture(environment);
      this.envMap = mapTex.envMap;
      this.envMap.name = environment.name;
      console.debug(
        `[DEBUG] EnvMap loaded in ${(performance.now() - t0).toFixed(0)} ms: ${environment.name
        }`
      );
      this.progress(35);

      // Hide Material Sample
      const sample = scene.getObjectByName('_root_ColorSample');
      if (sample != null) sample.visible = false;

      // Make root parent
      const wrap = new Group();
      wrap.add(scene);
      wrap.name = 'EV';

      ////////////////////////////
      // Load Mask and Hall on Cage Model
      ////////////////////////////
      if (this.loadedModel.component == 'Cage') {
        // Apply default envMap
        getAllMaterials(scene).forEach((mat) => {
          mat.envMap = this.envMap;
          mat.needsUpdate = true;
        });

        // Load Hall
        t0 = performance.now();
        const hallModel = await loader.loadAsync(
          'assets/models/' +
          this.loadedModel.type +
          '_Hall/' +
          this.loadedModel.type +
          '_Hall.glb'
        );
        console.debug(
          '[DEBUG] Model loaded in %d ms: %s',
          (performance.now() - t0).toFixed(0),
          this.loadedModel.type + '_Hall'
        );
        hallModel.scene.name = 'Hall_Objects';
        const hclips = hallModel.animations || [];

        // Hide Material Sample
        const sample = hallModel.scene.getObjectByName('_root_ColorSample');
        if (sample != null) sample.visible = false;

        // Hide unnecessary objects
        switch (this.loadedModel.type) {
          case 'R9':
            hallModel.scene.getObjectByName('R9_Hall_Far_Hide').visible = false;
            hallModel.scene.getObjectByName(
              'R9_Hall_WallColor_Hide'
            ).visible = false;
            hallModel.scene.getObjectByName(
              'R9_Hall_FloorColor_Hide'
            ).visible = false;
            break;

          case 'B750':
            hallModel.scene.getObjectByName(
              'B750_EVHall_Floor_Hide'
            ).visible = false;
            hallModel.scene.getObjectByName(
              'B750_EVHall_Other_Hide'
            ).visible = false;
            hallModel.scene.getObjectByName(
              'B750_Hall_Wall_far_Hide'
            ).visible = false;
            hallModel.scene.getObjectByName(
              'B750_Hall_WallColor_Hide'
            ).visible = false;
            break;
        }

        // Load outside Hall
        t0 = performance.now();
        const hall = await loader.loadAsync(
          getBaseDir(url) + '/' + this.loadedModel.type + '_Outside.glb'
        );
        console.debug(
          '[DEBUG] Model loaded in %d ms: %s_Outside',
          (performance.now() - t0).toFixed(0),
          this.loadedModel.type
        );
        this.progress(45);

        const hscene = hall.scene || hall.scenes[0];
        hscene.name = 'Hall';

        this.setContent(wrap, clips.concat(hclips));

        // Move cage to the anchor
        hallModel.scene.position.copy(
          hscene.getObjectByName('Hall_Anchor').position
        );
        hscene
          .getObjectByName(`${this.loadedModel.type}_Cage_Outside`)
          .add(hallModel.scene);

        // Apply default envMap
        getAllMaterials(hscene).forEach((mat) => {
          mat.envMap = this.envMap;
          mat.needsUpdate = true;
        });

        this.scene.getObjectByName('EV').add(hscene);

        // Mask
        const maskObj = hscene.getObjectByName('_Col_Hall');
        maskObj.material.colorWrite = false;
        maskObj.renderOrder = -1;

        this.progress(60);

        // Load mask
        t0 = performance.now();
        const mask = await loader.loadAsync(
          getBaseDir(url) + '/' + this.loadedModel.type + '_Mask.glb'
        );
        mask.scene.name = 'Mask';
        console.debug(
          '[DEBUG] Model loaded in %d ms: %s_Mask',
          (performance.now() - t0).toFixed(0),
          this.loadedModel.type
        );
        this.progress(70);

        mask.scene.traverse((m) => {
          if (m.type == 'Mesh') {
            m.material.colorWrite = false;
            m.renderOrder = -1;
          }
        });
        this.scene.getObjectByName('Cage').add(mask.scene);

        // Initialize Indicator
        await this.animator.setCageIndicator();
      } else {
        ////////////////////////////
        // Hall Model
        ////////////////////////////
        this.setContent(wrap, clips);

        // Load Collision & Mask
        t0 = performance.now();
        const collision = await loader.loadAsync(
          getBaseDir(url) + '/' + this.loadedModel.type + '_Hall_Collision.glb'
        );
        console.debug(
          '[DEBUG] Model loaded in %d ms: %s_Hall_Collision',
          (performance.now() - t0).toFixed(0),
          this.loadedModel.type
        );
        this.progress(55);

        const colscene = collision.scene || collision.scenes[0];
        colscene.name = 'Collision';
        colscene.traverse((m) => {
          if (m.type == 'Mesh') {
            m.material.colorWrite = false;
          }
        });
        this.scene.getObjectByName('EV').add(colscene);

        // Change Background Color
        this.state.bgColor1 = '#FFFFFF';
        this.state.bgColor2 = '#FFFFFF';
        this.updateBackground();

        // Apply default envMap
        getAllMaterials(scene).forEach((mat) => {
          mat.envMap = this.envMap;
          mat.needsUpdate = true;
        });

        // Update Lightmap
        switch (this.loadedModel.type) {
          case 'P11': {
            await this.updateLightMap(
              [
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_01_Floor.exr',
                  targets: [
                    '52_FrameFloor',
                    'P11_Hall_FloorColor',
                    'FloorColor',
                  ],
                },
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_02_Wall.exr',
                  targets: [
                    'P11_Hall_WallColor',
                    '51_FrameWall',
                    '63_HallPanel',
                    '64_HallIndicatorPanel',
                    '65_HallIndicator',
                    '66_HallButton',
                    '68_HallLantern',
                    '69_HallTenji',
                    'WallColor',
                  ],
                },
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_03_Frame.exr',
                  targets: ['61_HallFrame', '62_HallDoor', 'FrameColor'],
                },
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_04_Other.exr',
                  targets: ['P11_Hall_Far'],
                },
              ],
              'Hall'
            );
            break;
          }
          case 'P24': {
            await this.updateLightMap(
              [
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_01_Floor.exr',
                  targets: [
                    '52_FrameFloor',
                    'P24_Hall_FloorColor',
                    'FloorColor',
                  ],
                },
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_02_Wall.exr',
                  targets: [
                    'P24_Hall_WallColor',
                    '51_FrameWall',
                    '63_HallPanel',
                    '64_HallIndicatorPanel',
                    '65_HallIndicator',
                    '66_HallButton',
                    '68_HallLantern',
                    '69_HallTenji',
                    '71_InterphoneBox',
                    '72_HallMonitor',
                    '73_HallTenjiPanel',
                    '74_HallWCPanel',
                    '75_HallRenewalPanel',
                    'WallColor',
                  ],
                },
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_03_Frame.exr',
                  targets: ['61_HallFrame', '62_HallDoor', 'FrameColor'],
                },
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_04_Other.exr',
                  targets: ['P24_Hall_Far'],
                },
              ],
              'Hall'
            );
            break;
          }
          case 'R9': {
            await this.updateLightMap(
              [
                {
                  path: 'assets/models/R9_Hall/Lightmap/LM_R9_Hall_01_Floor.exr',
                  targets: [
                    'FloorColor',
                    'R9_Hall_FloorColor',
                    'R9_Hall_FloorColor_Hide',
                    '52_FrameFloor',
                  ],
                },
                {
                  path: 'assets/models/R9_Hall/Lightmap/LM_R9_Hall_02_Wall.exr',
                  targets: [
                    'WallColor',
                    'R9_Hall_WallColor',
                    'R9_Hall_WallColor_Hide',
                    '51_FrameWall',
                    '63_HallPanel',
                    '64_HallIndicatorPanel',
                    '65_HallIndicator',
                    '66_HallButton',
                    '68_HallLantern',
                    '69_HallTenji',
                  ],
                },
                {
                  path: 'assets/models/R9_Hall/Lightmap/LM_R9_Hall_03_Frame.exr',
                  targets: ['FrameColor', '61_HallFrame', '62_HallDoor'],
                },
                {
                  path: 'assets/models/R9_Hall/Lightmap/LM_R9_Hall_04_Other.exr',
                  targets: ['R9_Hall_Far', 'R9_Hall_Far_Hide'],
                },
              ],
              'Hall'
            );
            break;
          }
          case 'B750': {
            await this.updateLightMap(
              [
                {
                  path: 'assets/models/B750_Hall/Lightmap/LM_B750_Hall_01_Floor.exr',
                  targets: [
                    'FloorColor',
                    '52_FrameFloor',
                    'B750_EVHall_Floor_Hide',
                  ],
                },
                {
                  path: 'assets/models/B750_Hall/Lightmap/LM_B750_Hall_02_Wall.exr',
                  targets: [
                    'btn',
                    'WallColor',
                    'FrameColor',
                    'B750_Hall_WallColor',
                    'B750_Hall_WallColor_Hide',
                    'B750_Hall_Wall_far',
                    'B750_Hall_Wall_far_Hide',
                    '51_FrameWall',
                    '61_HallFrame',
                    '62_HallDoor',
                    '63_HallPanel',
                    '64_HallIndicatorPanel',
                    '65_HallIndicator',
                    '66_HallButton',
                    '68_HallLantern',
                    '69_HallTenji',
                  ],
                },
                {
                  path: 'assets/models/B750_Hall/Lightmap/LM_B750_Hall_03_Other.exr',
                  targets: ['B750_EVHall_Other', 'B750_EVHall_Other_Hide'],
                },
              ],
              'Hall'
            );
            break;
          }
          case 'EP26': {
            await this.updateLightMap(
              [
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_01_Floor.exr',
                  targets: [
                    '52_FrameFloor',
                    'EP26_Hall_FloorColor',
                    'FloorColor',
                  ],
                },
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_02_Wall.exr',
                  targets: [
                    'EP26_Hall_WallColor',
                    '51_FrameWall',
                    '63_HallPanel',
                    '64_HallIndicatorPanel',
                    '65_HallIndicator',
                    '66_HallButton',
                    '68_HallLantern',
                    '69_HallTenji',
                    '71_InterphoneBox',
                    '72_HallMonitor',
                    '73_HallTenjiPanel',
                    '74_HallWCPanel',
                    '75_HallRenewalPanel',
                    'WallColor',
                  ],
                },
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_03_Frame.exr',
                  targets: ['61_HallFrame', '62_HallDoor', 'FrameColor'],
                },
                {
                  path: 'assets/models/P11_Hall/Lightmap/LM_P11_Hall_04_Other.exr',
                  targets: ['EP26_Hall_Far'],
                },
              ],
              'Hall'
            );
            break;
          }
        }

        // Load Cage model
        t0 = performance.now();
        const hcage = await loader.loadAsync(
          'assets/models/' +
          this.loadedModel.type +
          '_Cage/' +
          this.loadedModel.type +
          '_Cage.glb'
        );
        console.debug(
          '[DEBUG] Model loaded in %d ms: %s',
          (performance.now() - t0).toFixed(0),
          this.loadedModel.type + '_Cage'
        );

        const cagescene = hcage.scene || hcage.scenes[0];
        const cageclips = hcage.animations || [];
        cagescene.name = 'Cage';

        // Hide collision
        const cageCol = cagescene.getObjectByName('_Col_Front');
        cageCol.material.transparent = true;
        cageCol.material.opacity = 0;

        // Hide Material Sample
        const sample = cagescene.getObjectByName('_root_ColorSample');
        if (sample != null) sample.visible = false;

        // Apply default envMap
        getAllMaterials(cagescene).forEach((mat) => {
          mat.envMap = this.envMap;
          mat.needsUpdate = true;
        });

        // Rotate cage
        cagescene.rotation.set(0, Math.PI, 0);

        // Move cage to the anchor
        cagescene.position.copy(
          this.scene.getObjectByName('Cage_Anchor').position
        );

        // Add Animation clips
        this.setClips(this.clips.concat(cageclips));
        this.updateGUI();

        this.scene.getObjectByName('EV').add(cagescene);

        this.progress(65);
      }

      ////////////////////////
      // Load structure json
      ////////////////////////
      this.progress(80);
      const response = await fetch(getBaseDir(url) + '/structure.json');
      const jsonData = await response.json();

      this.jsonData = jsonData;

      // Build HTML
      jsonData.forEach((category) => {
        let patternCount = 0;
        const UI = document.getElementById('custom_ui');

        // headline
        if (category.id == 'ceiling') {
          const HDiv = document.createElement('div');
          HDiv.className = 'category_header';

          const HImg = document.createElement('img');
          HImg.setAttribute(
            'src',
            'assets/images/00_navigation/nav_cus_tx1.png'
          );
          HDiv.appendChild(HImg);
          UI.appendChild(HDiv);
        } else if (category.id == 'door') {
          const HDiv = document.createElement('div');
          HDiv.className = 'category_header';
          HDiv.style.marginTop = '4%';

          const HImg = document.createElement('img');
          HImg.setAttribute(
            'src',
            'assets/images/00_navigation/nav_cus_tx2.png'
          );
          HDiv.appendChild(HImg);
          UI.appendChild(HDiv);
        }

        // Add headline
        const catWrap = document.createElement('div');
        catWrap.className = 'category_wrap';

        const catImg = document.createElement('img');
        catImg.setAttribute('src', 'assets/images/' + category.image_path);
        catWrap.appendChild(catImg);

        const catDiv = document.createElement('div');
        catDiv.className = 'category';
        if (
          category.id == 'sidepanel' ||
          category.id == 'other' ||
          category.id == 'wc'
        )
          catDiv.style.alignItems = 'baseline';
        catWrap.appendChild(catDiv);

        UI.appendChild(catWrap);

        // parts
        if (!Array.isArray(category.parts)) category.parts = [category.parts];
        category.parts.forEach((part) => {
          patternCount += part.patterns.length;
          let rightFlag = false;
          if (patternCount > 6) patternCount = part.patterns.length;
          else if (patternCount == 6) {
            rightFlag = true;
            patternCount = 0;
          }

          // Create div
          const partWrap = document.createElement('div');
          partWrap.className = 'part_wrap';
          if (part.id == 'designmat') partWrap.classList.add('part_forcewidth');

          // Set img
          if (part.image_path != null) {
            const partImg = document.createElement('img');
            partImg.setAttribute('src', 'assets/images/' + part.image_path);
            if (part.id == 'designmat')
              partImg.classList.add('part_forcewidth');
            partWrap.appendChild(partImg);
          }

          const partDiv = document.createElement('div');
          partDiv.className = 'part';
          partWrap.appendChild(partDiv);

          // Align if it needs
          if (
            part.id == 'mainpanel_button_position' ||
            part.id == 'Hall_Interphone' ||
            part.id == 'light'
          ) {
            const spacer = document.createElement('div');
            spacer.style.width = '100%';
            catDiv.appendChild(spacer);
          }
          catDiv.appendChild(partWrap);

          // patterns
          part.patterns.forEach((pattern, i) => {
            const patDiv = document.createElement('div');
            patDiv.className = 'pattern';

            // fukidashi position
            if ((i == part.patterns.length - 1 && rightFlag) || i % 6 == 5)
              patDiv.style.justifyContent = 'right';
            switch (pattern.id) {
              case 'ind_button_P21SHU':
              case 'ind_RNdesign_SASH':
              case 'floor_Frost':
              case 'designSheet_color_GrayMarble':
              case 'frame_color_OrientRed':
              case 'door_color_OrientRed':
              case 'maku_color_OrientRed':
              case 'mainpanel_cover_GrayishWhite': {
                patDiv.style.justifyContent = 'left';
                break;
              }
              case 'ind_button_P14FUL':
              case 'ind_RNdesign_SWSV':
              case 'wall_floor_LR_AshDarkGray':
              case 'wall_floor_Back_AshDarkGray':
              case 'door_color_AshDarkGray':
              case 'floor_StoneBlack':
              case 'frame_color_SoftBeige':
              case 'door_color_SoftBeige':
              case 'maku_color_SoftBeige':
              case 'ind_lantern_kumikomi_orange': {
                patDiv.style.justifyContent = 'right';
                break;
              }
              case 'mainpanel_layout_7':
              case 'ind_lantern_VLS125_white':
              case 'ind_lantern_kumikomi_white': {
                patDiv.style.justifyContent = '';
                break;
              }
            }

            // radios label
            const labelEl = document.createElement('label');
            labelEl.setAttribute('for', pattern.id);
            const labelImg = document.createElement('img');
            labelImg.setAttribute('src', 'assets/images/' + pattern.image_path);
            labelImg.onload = function () {
              labelImg.parentNode.style.height = labelImg.clientHeight + 'px';
              labelImg.parentNode.nextSibling.style.marginTop =
                labelImg.clientHeight + 'px';
              document.querySelectorAll('.UIArrow').forEach((arrow) => {
                arrow.style.height = arrow.parentNode.style.height =
                  labelImg.clientHeight + 'px';
              });
            };
            labelEl.appendChild(labelImg);

            // radios
            const Radio = document.createElement('input');
            Radio.setAttribute('type', 'radio');
            Radio.setAttribute('id', pattern.id);
            Radio.setAttribute('name', part.id);
            Radio.setAttribute('value', pattern.id);
            Radio.setAttribute(
              'onChange',
              "updatePatterns('" + pattern.id + "')"
            );

            // fukidashi
            const FDiv = document.createElement('div');
            FDiv.className = 'fukidashi';
            const FImg = document.createElement('img');
            FImg.src = 'assets/images/' + pattern.fukidashi_path;
            FDiv.appendChild(FImg);
            switch (pattern.id) {
              case 'frame_color_FreshWhite':
              case 'door_color_FreshWhite':
              case 'maku_color_FreshWhite':
              case 'frame_color_DecoDarkGray':
              case 'door_color_DecoDarkGray':
              case 'maku_color_DecoDarkGray':
              case 'designPanel_color_Beige':
              case 'designPanel_color_DarkMetalic':
              case 'ind_lantern_kumikomi_white':
                FImg.style.transform = 'translate(-22%)';
                break;
              case 'floor_HV_Gray':
                FImg.style.transform = 'translate(-21.7%)';
                break;
              case 'frame_color_OrientRed':
              case 'door_color_OrientRed':
              case 'maku_color_OrientRed':
                FImg.style.transform = 'translate(-6%)';
                break;
              case 'ind_RNdesign_SABronze':
              case 'floor_Frost':
              case 'designSheet_color_GrayMarble':
                FImg.style.transform = 'translate(-9.1%)';
                break;
              case 'ind_RNdesign_SASilver':
              case 'designSheet_color_DarkBrown':
                FImg.style.transform = 'translate(7.6%)';
                break;
              case 'ind_lantern_kumikomi_orange':
                FImg.style.transform = 'translate(16%)';
                break;
              case 'frame_design_SSI':
              case 'floor_StoneBlack':
              case 'frame_color_SoftBeige':
              case 'door_color_SoftBeige':
              case 'maku_color_SoftBeige':
                FImg.style.transform = 'translate(20%)';
                break;
              case 'ind_RNdesign_SAGold':
              case 'wall_floor_LR_AshDarkGray':
              case 'wall_floor_Back_AshDarkGray':
              case 'door_color_AshDarkGray':
                FImg.style.transform = 'translate(24.4%)';
                break;
              case 'frame_color_DarkBrown':
              case 'door_color_DarkBrown':
              case 'maku_color_DarkBrown':
                FImg.style.transform = 'translate(34.5%)';
                break;
              case 'frame_design_Smart':
                FImg.style.transform = 'translate(35%)';
                break;

              case 'frame_design_SS':
                if (this.loadedModel.type == 'B750')
                  FImg.style.transform = 'translate(35.3%)';
                break;
              case 'frame_color_GrayishWhite':
              case 'maku_color_GrayishWhite':
                FImg.style.transform = 'translate(37%)';
                break;
              case 'frame_color_DecoGrayishWhite':
              case 'door_color_DecoGrayishWhite':
              case 'maku_color_DecoGrayishWhite':
                FImg.style.transform = 'translate(39%)';
                break;
            }

            // Indicator
            const iDiv = document.createElement('div');
            iDiv.classList.add('indicator');
            labelEl.appendChild(iDiv);

            patDiv.appendChild(Radio);
            patDiv.appendChild(labelEl);
            patDiv.appendChild(FDiv);

            partDiv.appendChild(patDiv);

            // Floor UI
            if (
              pattern.id == 'mainpanel_layout_2' ||
              pattern.id == 'mainpanel_layout_12'
            ) {
              const UIArrow = document.createElement('div');
              const UIArrowImg = document.createElement('img');
              UIArrowImg.className = 'UIArrow';
              UIArrow.appendChild(UIArrowImg);

              if (pattern.id == 'mainpanel_layout_2') {
                UIArrowImg.src =
                  'assets/images/00_navigation/nav_cus_sec11_btn_left.png';
                UIArrowImg.setAttribute('onclick', 'updateFloorUI("Sub")');
                partDiv.prepend(UIArrow);
              } else {
                UIArrowImg.src =
                  'assets/images/00_navigation/nav_cus_sec11_btn_right.png';
                UIArrowImg.setAttribute('onclick', 'updateFloorUI("Add")');
                partDiv.append(UIArrow);
              }
            }
          });
        });

        this.resize();
      });

      // Pattern initialize
      if (this.loadedModel.component == 'Hall') {
        // Update cage patterns on Hall scene
        await this.updatePatterns(
          null,
          -1,
          new Object({
            name: this.loadedModel.type + '_Cage',
            type: this.loadedModel.type,
            component: 'Cage',
          })
        );
      } else {
        // Update cage patterns on Cage scene
        await this.updatePatterns(
          null,
          -1,
          new Object({
            name: this.loadedModel.type + '_Hall',
            type: this.loadedModel.type,
            component: 'Hall',
          })
        );
      }
      this.progress(90);

      await this.updatePatterns(null, -1);
      this.progress(100);

      this.viewController = new ViewController(
        this.jsonData,
        this.loadedModel,
        this.controls,
        this.defaultCamera
      );
      this.viewController.changeControl();
      this.viewController.setCamPreset(5);

      blobURLs.forEach(URL.revokeObjectURL);

      // See: https://github.com/google/draco/issues/349
      // DRACOLoader.releaseDecoderModule();

      // console.log(this.scene)

      resolve(gltf);
    });
  }

  /**
   * @param {THREE.Object3D} object
   * @param {Array<THREE.AnimationClip} clips
   */
  setContent(object, clips) {
    this.clear();

    const box = new Box3().setFromObject(object);
    const size = box.getSize(new Vector3()).length();
    const center = box.getCenter(new Vector3());

    this.controls.reset();

    object.position.x += object.position.x - center.x;
    object.position.y += object.position.y - center.y;
    object.position.z += object.position.z - center.z;
    this.controls.maxDistance = size * 10;
    this.defaultCamera.near = size / 100;
    this.defaultCamera.far = size * 100;
    this.defaultCamera.updateProjectionMatrix();

    if (this.options.cameraPosition) {
      this.defaultCamera.position.fromArray(this.options.cameraPosition);
      this.defaultCamera.lookAt(new Vector3());
    } else {
      this.defaultCamera.position.copy(center);
      // this.defaultCamera.position.x += size / 2.0;
      // this.defaultCamera.position.y += size / 5.0;
      // this.defaultCamera.position.z += size / 2.0;
      if (this.loadedModel.component == 'Cage') {
        this.defaultCamera.position.x = 3.9;
        this.defaultCamera.position.y = 0;
        this.defaultCamera.position.z = -3.9;
        this.defaultCamera.lookAt(center);
      } else {
        switch (this.loadedModel.type) {
          case 'P11': {
            this.defaultCamera.position.set(2.94, -1.28, -2.53);
            this.controls.target.set(-0.14, -1.28, 2.04);
            break;
          }
          case 'R9': {
            this.defaultCamera.position.set(1.2, -0.59, -1.34);
            this.controls.target.set(-2.43, -0.51, 2.1);
            break;
          }
          case 'B750': {
            this.defaultCamera.position.set(3.36, -0.41, -0.5);
            this.controls.target.set(1.15, -0.43, 1.85);
            break;
          }
        }
      }
    }

    this.setCamera(DEFAULT_CAMERA);

    this.axesCamera.position.copy(this.defaultCamera.position);
    this.axesCamera.lookAt(this.axesScene.position);
    this.axesCamera.near = size / 100;
    this.axesCamera.far = size * 100;
    this.axesCamera.updateProjectionMatrix();
    this.axesCorner.scale.set(size, size, size);

    this.controls.saveState();

    this.scene.add(object);
    this.content = object;

    this.state.addLights = true;

    this.content.traverse((node) => {
      if (node.isLight) {
        this.state.addLights = false;
      } else if (node.isMesh) {
        // TODO(https://github.com/mrdoob/three.js/pull/18235): Clean up.
        node.material.depthWrite = !node.material.transparent;
      }
    });

    this.setClips(clips);

    this.updateLights();
    this.updateGUI();
    this.updateEnvironment();
    this.updateTextureEncoding();
    this.updateDisplay();

    // window.content = this.content;
    // console.info('[glTF Viewer] THREE.Scene exported as `window.content`.');
    // this.printGraph(this.content);
  }

  printGraph(node) {
    console.groupCollapsed(' <' + node.type + '> ' + node.name);
    node.children.forEach((child) => this.printGraph(child));
    console.groupEnd();
  }

  /**
   * @param {Array<THREE.AnimationClip} clips
   */
  setClips(clips) {
    if (this.mixer) {
      this.mixer.stopAllAction();
      this.mixer.uncacheRoot(this.mixer.getRoot());
      this.mixer = null;
    }

    this.clips = clips;
    if (!clips.length) return;

    this.mixer = new AnimationMixer(this.content);

    this.animator = new Animator(
      this.scene,
      this.loadedModel,
      this.state,
      this.controls,
      this.mixer,
      this.clips
    );
    this.animateParam = this.animator.animateParam;
  }

  playAllClips() {
    this.clips.forEach((clip) => {
      this.mixer.clipAction(clip).reset().play();
      this.state.actionStates[clip.name] = true;
    });
  }

  /**
   * @param {string} name
   */
  setCamera(name) {
    if (name === DEFAULT_CAMERA) {
      this.controls.enabled = false;
      this.activeCamera = this.defaultCamera;
    } else {
      this.controls.enabled = false;
      this.content.traverse((node) => {
        if (node.isCamera && node.name === name) {
          this.activeCamera = node;
        }
      });
    }
  }

  updateTextureEncoding() {
    const encoding =
      this.state.textureEncoding === 'sRGB' ? sRGBEncoding : LinearEncoding;
    traverseMaterials(this.content, (material) => {
      if (material.map) material.map.encoding = encoding;
      if (material.emissiveMap) material.emissiveMap.encoding = encoding;
      if (material.map || material.emissiveMap) material.needsUpdate = true;
    });
  }

  updateLights() {
    const state = this.state;
    const lights = this.lights;

    if (state.addLights && !lights.length) {
      this.addLights();
    } else if (!state.addLights && lights.length) {
      this.removeLights();
    }

    this.renderer.toneMappingExposure = state.toneMapExposure;

    lights.forEach((light) => {
      switch (light.type) {
        case 'AmbientLight':
          light.intensity = state.ambientIntensity;
          light.color.setHex(state.ambientColor);
          break;
        case 'DirectionalLight':
          light.intensity = state.directIntensity;
          light.color.setHex(state.directColor);
          break;
        case 'PointLight':
          light.intensity = state.pointIntensity;
          light.color.setHex(state.pointColor);
          light.distance = state.pointDistance;
          light.decay = state.pointDecay;
          break;
        default:
          break;
      }
    });
  }

  addLights() {
    const state = this.state;

    if (this.options.preset === Preset.ASSET_GENERATOR) {
      const hemiLight = new HemisphereLight();
      hemiLight.name = 'hemi_light';
      this.scene.add(hemiLight);
      this.lights.push(hemiLight);
      return;
    }

    // Ambient Light
    const light1 = new AmbientLight(state.ambientColor, state.ambientIntensity);
    light1.name = 'ambient_light';
    this.defaultCamera.add(light1);

    // Directional Light
    const light2 = new DirectionalLight(
      state.directColor,
      state.directIntensity
    );
    light2.position.set(0.5, 2, 0.866); // ~60º
    light2.name = 'directional_light';
    this.defaultCamera.add(light2);

    // Point Light
    const light3 = new PointLight(
      state.pointColor,
      state.pointIntensity,
      state.pointDistance,
      state.pointDecay
    );
    const ceiling = this.scene.getObjectByName('_Col_Top');

    if (this.loadedModel.component == 'Cage') {
      // Set position to cage's ceiling
      light3.position.set(
        ceiling.position.x,
        ceiling.position.y - 1.0,
        ceiling.position.z
      );
    } else {
      // Set intensity 0 on Hall models
      state.pointIntensity = 0;
      light3.intensity = state.pointIntensity;
    }
    light3.name = 'point_light';
    this.scene.add(light3);

    this.lights.push(light1, light2, light3);
  }

  removeLights() {
    this.lights.forEach((light) => light.parent.remove(light));
    this.lights.length = 0;
  }

  updateEnvironment() {
    const environment = environments.filter(
      (entry) => entry.name === this.state.environment
    )[0];

    this.getCubeMapTexture(environment).then(({ envMap }) => {
      envMap = null;

      if (
        (!envMap || !this.state.background) &&
        this.activeCamera === this.defaultCamera
      ) {
        this.scene.add(this.vignette);
      } else {
        this.scene.remove(this.vignette);
      }

      this.scene.environment = envMap;
      this.scene.background = this.state.background ? envMap : null;
    });
  }

  getCubeMapTexture(environment) {
    const { path, format } = environment;

    // no envmap
    if (!path) return Promise.resolve({ envMap: null });

    // HDR
    switch (format) {
      case '.hdr': {
        return new Promise((resolve, reject) => {
          new RGBELoader().setDataType(UnsignedByteType).load(
            path,
            (texture) => {
              const envMap =
                this.pmremGenerator.fromEquirectangular(texture).texture;
              this.pmremGenerator.dispose();

              resolve({ envMap });
            },
            undefined,
            reject
          );
        });
      }
      case '.png': {
        return new Promise((resolve, reject) => {
          new TextureLoader().load(
            path,
            (texture) => {
              texture.mapping = EquirectangularReflectionMapping;
              texture.flipY = true;
              texture.magFilter = NearestFilter;
              texture.minFilter = NearestFilter;
              texture.encoding = sRGBEncoding;
              const envMap = texture;

              resolve({ envMap });
            },
            undefined,
            reject
          );
        });
      }
      default: {
        if (!path) return Promise.resolve({ envMap: null });
      }
    }
  }

  updateDisplay() {
    if (this.skeletonHelpers.length) {
      this.skeletonHelpers.forEach((helper) => this.scene.remove(helper));
    }

    traverseMaterials(this.content, (material) => {
      material.wireframe = this.state.wireframe;
    });

    this.content.traverse((node) => {
      if (node.isMesh && node.skeleton && this.state.skeleton) {
        const helper = new SkeletonHelper(node.skeleton.bones[0].parent);
        helper.material.linewidth = 3;
        this.scene.add(helper);
        this.skeletonHelpers.push(helper);
      }
    });

    if (this.state.grid !== Boolean(this.gridHelper)) {
      if (this.state.grid) {
        this.gridHelper = new GridHelper();
        this.axesHelper = new AxesHelper();
        this.axesHelper.renderOrder = 999;
        this.axesHelper.onBeforeRender = (renderer) => renderer.clearDepth();
        this.scene.add(this.gridHelper);
        this.scene.add(this.axesHelper);
      } else {
        this.scene.remove(this.gridHelper);
        this.scene.remove(this.axesHelper);
        this.gridHelper = null;
        this.axesHelper = null;
        this.axesRenderer.clear();
      }
    }
  }

  updateBackground() {
    this.vignette.style({ colors: [this.state.bgColor1, this.state.bgColor2] });
  }

  /**
   * Adds AxesHelper.
   *
   * See: https://stackoverflow.com/q/16226693/1314762
   */
  addAxesHelper() {
    this.axesDiv = document.createElement('div');
    this.el.appendChild(this.axesDiv);
    this.axesDiv.classList.add('axes');

    const { clientWidth, clientHeight } = this.axesDiv;

    this.axesScene = new Scene();
    this.axesCamera = new PerspectiveCamera(
      50,
      clientWidth / clientHeight,
      0.1,
      10
    );
    this.axesScene.add(this.axesCamera);

    this.axesRenderer = new WebGLRenderer({ alpha: true });
    this.axesRenderer.setPixelRatio(window.devicePixelRatio);
    this.axesRenderer.setSize(
      this.axesDiv.clientWidth,
      this.axesDiv.clientHeight
    );

    this.axesCamera.up = this.defaultCamera.up;

    this.axesCorner = new AxesHelper(5);
    this.axesScene.add(this.axesCorner);
    this.axesDiv.appendChild(this.axesRenderer.domElement);
  }

  addGUI() {
    const gui = (this.gui = new GUI({
      autoPlace: false,
      width: 260,
      hideable: true,
    }));

    // Display controls.
    const dispFolder = gui.addFolder('Display');
    const envBackgroundCtrl = dispFolder.add(this.state, 'background');
    envBackgroundCtrl.onChange(() => this.updateEnvironment());
    const wireframeCtrl = dispFolder.add(this.state, 'wireframe');
    wireframeCtrl.onChange(() => this.updateDisplay());
    const skeletonCtrl = dispFolder.add(this.state, 'skeleton');
    skeletonCtrl.onChange(() => this.updateDisplay());
    const gridCtrl = dispFolder.add(this.state, 'grid');
    gridCtrl.onChange(() => this.updateDisplay());
    dispFolder.add(this.controls, 'autoRotate');
    dispFolder.add(this.controls, 'screenSpacePanning');
    const bgColor1Ctrl = dispFolder.addColor(this.state, 'bgColor1');
    const bgColor2Ctrl = dispFolder.addColor(this.state, 'bgColor2');
    bgColor1Ctrl.onChange(() => this.updateBackground());
    bgColor2Ctrl.onChange(() => this.updateBackground());

    // Lighting controls.
    const lightFolder = gui.addFolder('Lighting');
    // const encodingCtrl = lightFolder.add(this.state, 'textureEncoding', ['sRGB', 'Linear']);
    // encodingCtrl.onChange(() => this.updateTextureEncoding());
    lightFolder
      .add(this.renderer, 'outputEncoding', {
        sRGB: sRGBEncoding,
        Linear: LinearEncoding,
      })
      .onChange(() => {
        this.renderer.outputEncoding = Number(this.renderer.outputEncoding);
        traverseMaterials(this.content, (material) => {
          material.needsUpdate = true;
        });
      });
    // const envMapCtrl = lightFolder.add(this.state, 'environment', environments.map((env) => env.name));
    const envMapCtrl = lightFolder.add(this.state, 'environment', 'None');
    envMapCtrl.onChange(() => this.updateEnvironment());
    [
      lightFolder.add(this.state, 'toneMapExposure', 0, 2),
      lightFolder.add(this.state, 'addLights').listen(),
      lightFolder.addColor(this.state, 'ambientColor'),
      lightFolder.add(this.state, 'ambientIntensity', 0, 2),
      lightFolder.addColor(this.state, 'directColor'),
      lightFolder.add(this.state, 'directIntensity', 0, 4), // TODO(#116)
      lightFolder.addColor(this.state, 'pointColor'),
      lightFolder.add(this.state, 'pointIntensity', 0, 3),
      lightFolder.add(this.state, 'pointDistance', 0, 5),
      lightFolder.add(this.state, 'pointDecay', 0, 30),
    ].forEach((ctrl) => ctrl.onChange(() => this.updateLights()));
    const lmIntCtrl = lightFolder.add(this.state, 'lightMapIntensity', 0, 30);
    lmIntCtrl.onChange(() => {
      const materials = getAllMaterials(this.scene.getObjectByName('EV'));
      materials.forEach((material) => {
        material.lightMapIntensity = this.state.lightMapIntensity;
        material.needsUpdate = true;
      });
    });
    const amIntCtrl = lightFolder.add(this.state, 'aoMapIntensity', 0, 1);
    amIntCtrl.onChange(() => {
      const materials = getAllMaterials(this.scene.getObjectByName('EV'));
      materials.forEach((material) => {
        material.aoMapIntensity = this.state.aoMapIntensity;
        material.needsUpdate = true;
      });
    });

    // Animation controls.
    this.animFolder = gui.addFolder('Animation');
    this.animFolder.domElement.style.display = 'none';
    const playbackSpeedCtrl = this.animFolder.add(
      this.state,
      'playbackSpeed',
      0,
      1
    );
    playbackSpeedCtrl.onChange((speed) => {
      if (this.mixer) this.mixer.timeScale = speed;
    });
    this.animFolder.add({ playAll: () => this.playAllClips() }, 'playAll');

    // Morph target controls.
    this.morphFolder = gui.addFolder('Morph Targets');
    this.morphFolder.domElement.style.display = 'none';

    // Camera controls.
    this.cameraFolder = gui.addFolder('Cameras');
    this.cameraFolder.domElement.style.display = 'none';

    // Stats.
    const perfFolder = gui.addFolder('Performance');
    const perfLi = document.createElement('li');
    this.stats.dom.style.position = 'static';
    perfLi.appendChild(this.stats.dom);
    perfLi.classList.add('gui-stats');
    perfFolder.__ul.appendChild(perfLi);

    const guiWrap = document.createElement('div');
    this.el.appendChild(guiWrap);
    guiWrap.classList.add('gui-wrap');
    guiWrap.appendChild(gui.domElement);
    gui.open();

    // Hide GUI
    GUI.toggleHide();
    window.toggleGui = GUI.toggleHide.bind(this);
  }

  updateGUI() {
    this.cameraFolder.domElement.style.display = 'none';

    this.morphCtrls.forEach((ctrl) => ctrl.remove());
    this.morphCtrls.length = 0;
    this.morphFolder.domElement.style.display = 'none';

    this.animCtrls.forEach((ctrl) => ctrl.remove());
    this.animCtrls.length = 0;
    this.animFolder.domElement.style.display = 'none';

    const cameraNames = [];
    const morphMeshes = [];
    this.content.traverse((node) => {
      if (node.isMesh && node.morphTargetInfluences) {
        morphMeshes.push(node);
      }
      if (node.isCamera) {
        node.name = node.name || `VIEWER__camera_${cameraNames.length + 1}`;
        cameraNames.push(node.name);
      }
    });

    if (cameraNames.length) {
      this.cameraFolder.domElement.style.display = '';
      if (this.cameraCtrl) this.cameraCtrl.remove();
      const cameraOptions = [DEFAULT_CAMERA].concat(cameraNames);
      this.cameraCtrl = this.cameraFolder.add(
        this.state,
        'camera',
        cameraOptions
      );
      this.cameraCtrl.onChange((name) => this.setCamera(name));
    }

    if (morphMeshes.length) {
      this.morphFolder.domElement.style.display = '';
      morphMeshes.forEach((mesh) => {
        if (mesh.morphTargetInfluences.length) {
          const nameCtrl = this.morphFolder.add(
            { name: mesh.name || 'Untitled' },
            'name'
          );
          this.morphCtrls.push(nameCtrl);
        }
        for (let i = 0; i < mesh.morphTargetInfluences.length; i++) {
          const ctrl = this.morphFolder
            .add(mesh.morphTargetInfluences, i, 0, 1, 0.01)
            .listen();
          Object.keys(mesh.morphTargetDictionary).forEach((key) => {
            if (key && mesh.morphTargetDictionary[key] === i) ctrl.name(key);
          });
          this.morphCtrls.push(ctrl);
        }
      });
    }

    if (this.clips.length) {
      this.animFolder.domElement.style.display = '';
      const actionStates = (this.state.actionStates = {});
      this.clips.forEach((clip, clipIndex) => {
        clip.name = `${clipIndex + 1}. ${clip.name}`;

        // Autoplay the first clip.
        let action;
        if (clipIndex === -1) {
          //disable
          actionStates[clip.name] = true;
          action = this.mixer.clipAction(clip);
          action.play();
        } else {
          actionStates[clip.name] = false;
        }

        // Play other clips when enabled.
        const ctrl = this.animFolder.add(actionStates, clip.name).listen();
        ctrl.onChange((playAnimation) => {
          action = action || this.mixer.clipAction(clip);
          action.setEffectiveTimeScale(1);
          playAnimation ? action.play() : action.stop();
        });
        this.animCtrls.push(ctrl);
      });
    }
  }

  clear() {
    if (!this.content) return;

    this.scene.remove(this.content);

    // dispose geometry
    this.content.traverse((node) => {
      if (!node.isMesh) return;

      node.geometry.dispose();
    });

    // dispose textures
    traverseMaterials(this.content, (material) => {
      MAP_NAMES.forEach((map) => {
        if (material[map]) material[map].dispose();
      });
    });
  }

  /**
   * モデルのパターン更新関数
   * @param {string} id 更新先ID 未指定の場合はプリセットモード
   * @param {number} preset プリセットID -1は可能であればストレージからロード 0以上の場合はプリセットのロード 0は環境マップレンダリング用
   * @param {string} modearg 動作モード CageまたはHallで更新先を変更
   */
  async updatePatterns(id, preset, modearg) {
    this.showSpinner();
    let t0 = performance.now();

    // Mode initialize
    let mode = this.loadedModel;
    let modeJson = null;
    if (modearg) {
      mode = modearg;
      const response = await fetch(`assets/models/${mode.name}/structure.json`);
      modeJson = await response.json();

      modeJson.forEach((category) => {
        if (!Array.isArray(category.parts)) category.parts = [category.parts];
      });
    }

    // Reset sui_ind
    document.querySelectorAll('.sui_ind').forEach((ind) => {
      ind.classList.remove('active');
    });

    // Preset button pressed
    if (preset >= 1) {
      sessionStorage.removeItem('isEBCoverInit');
      document
        .querySelectorAll('.sui_ind')
      [preset - 1]?.classList.add('active');
    } else {
      document.querySelectorAll('.sui_ind').forEach((ind) => {
        ind.classList.remove('active');
      });
    }

    // init active ids
    let activeIds = new Object();

    // Change Pattern
    if (id != null) {
      // Pattern changed
      const selectedCat = this.getPatternsById(id);

      // Lightmap status
      if (
        selectedCat.parts.type == 'Lightmap' ||
        selectedCat.parts.patterns[0].lightmap ||
        selectedCat.parts.id == 'ceiling_color'
      )
        this.lightMapState.needsUpdate = true;

      // Get current value
      Array.from(
        document.getElementById('custom_ui').getElementsByTagName('input')
      ).forEach((input) => {
        if (input.type == 'radio' && input.checked) {
          if (input.name == selectedCat.parts.id) {
            // Insert new selected value
            activeIds[input.name] = id;
          } else {
            activeIds[input.name] = input.value;
          }
        }
      });

      // Try to fill no patterns by default value
      Object.keys(this.Presets[mode.name].slice(-1)[0]).forEach((key) => {
        if (activeIds[key] == '' || activeIds[key] === undefined) {
          // avoid RN-IND and ind conflict
          if (
            key == 'ind_design' &&
            document.querySelector(
              `input[type='radio'][name='Ind_RNPanel']:checked`
            )
          )
            return;
          activeIds[key] = this.Presets[mode.name].slice(-1)[0][key];
          if (id == 'frame_design_TLM' && key == 'maku_color') {
            activeIds['frame_color'] = 'frame_color_Hairline';
            activeIds['door_color'] = 'door_color_Hairline';
          }
        }
      });

      // For cage models
      if (mode.component == 'Cage') {
        // Copy status
        if (id == 'wc_main_On' && activeIds['EBCover_side'] != '') {
          activeIds['wc_EBCover_side'] =
            'wc_EBCover_side_' + activeIds['EBCover_side'].split('_').pop();
        }

        if (activeIds['wc_main'] == 'wc_main_On') {
          activeIds['handrail_type'] = '';
          activeIds['sidepanel_design'] = '';
          // activeIds['sidepanel_button'] = "";
          activeIds['Mirror_design'] = '';
          activeIds['handrail_position'] = '';
          activeIds['EBCover_side'] = '';
        } else {
          activeIds['wc_handrail_type'] = '';
          activeIds['wc_sidepanel_design'] = '';
          // activeIds['wc_sidepanel_button'] = "";
          activeIds['wc_Mirror_design'] = '';
          activeIds['wc_handrail_position'] = '';
          activeIds['wc_EBCover_side'] = '';
        }

        // Override
        if (id == 'wc_main_Off') {
          activeIds['handrail_position'] = 'handrail_position_Off';
          activeIds['sidepanel_position'] = 'sidepanel_position_Off';
          activeIds['Mirror_design'] = 'Mirror_design_Off';
          activeIds['EBCover_side'] = 'EBCover_side_Off';
        } else if (id == 'Trunk_On') {
          activeIds['designsheet_meji'] = 'designSheet_Meji_Off';
          activeIds['designsheet_desgin'] = 'designSheet_design_Off';
        }

        // Sync sidepanel cover color
        if (/^(mainpanel_cover|wc_main_On)/.test(id)) {
          // const color = id.split('_').pop();
          // activeIds['mainpanel_cover'] = 'mainpanel_cover_' + color;

          if (activeIds['wc_sidepanel_design'] != '') {
            const color = activeIds['mainpanel_cover'].split('_').pop();
            activeIds['wc_sidepanel_design'] = 'wc_sidepanel_design_' + color;
          }
          if (activeIds['sidepanel_design'] != '') {
            const color = activeIds['mainpanel_cover'].split('_').pop();
            activeIds['sidepanel_design'] = 'sidepanel_design_' + color;
          }
        }

        // Link
        const links = this.getPatternsById(id)?.parts.patterns.filter(
          (pattern) => pattern.id == id
        )[0].links;
        if (links) {
          links.forEach((linkId) => {
            const linkPartId = this.getPatternsById(linkId)?.parts.id;
            if (linkPartId) activeIds[linkPartId] = linkId;
            else console.warn(`Link ID ${linkId} does not found.`);
          });
        }

        // Sync button pattern
        // if (/^(wc_sidepanel_button|sidepanel_button)/.test(id)) {
        //   const button = id.split('_').pop();
        //   // activeIds['mainpanel_button'] = 'mainpanel_button_' + button;

        //   if (activeIds['wc_sidepanel_button'] != "") {
        //     // const button = activeIds['mainpanel_button'].split('_').pop();
        //     activeIds['wc_sidepanel_button'] = 'wc_sidepanel_button_' + button;
        //   }
        //   if (activeIds['sidepanel_button'] != "") {
        //     // const button = activeIds['mainpanel_button'].split('_').pop();
        //     activeIds['sidepanel_button'] = 'sidepanel_button_' + button;
        //   }
        // }

        // EBCover first sync
        if (/^EBCover_main/.test(id) && !sessionStorage['isEBCoverInit']) {
          // Sidepanel is visible
          if (
            activeIds['sidepanel_position'] != 'sidepanel_position_Off' &&
            activeIds['sidepanel_position'] != ''
          ) {
            const stat = activeIds['EBCover_main'].split('_').pop();
            activeIds['EBCover_side'] = 'EBCover_side_' + stat;
            activeIds['wc_EBCover_side'] = 'wc_EBCover_side_' + stat;
            sessionStorage['isEBCoverInit'] = true;
          }
        }
      }
      //For hall models
      else {
        // Indicator
        if (/^(ind_design_)/.test(id)) {
          const radio = document.getElementById(activeIds['Ind_RNPanel']);
          if (radio) radio.checked = false;
          delete activeIds['Ind_RNPanel'];
        } else if (/^(ind_RNdesign_)/.test(id)) {
          const radio = document.getElementById(activeIds['ind_design']);
          if (radio) radio.checked = false;
          delete activeIds['ind_design'];
        }
      }
    } else {
      this.lightMapState.needsUpdate = true;

      // Load specified preset
      if (preset >= 0 || preset === undefined) {
        if (preset === undefined) {
          // Reset
          activeIds = JSON.parse(
            JSON.stringify(this.Presets[mode.name].slice(-1)[0])
          );
        } else {
          activeIds = JSON.parse(
            JSON.stringify(this.Presets[mode.name][preset])
          );
        }
        this.currentPreset = preset;

        // For cage models
        if (mode.component == 'Cage') {
          if (activeIds['wc_main'] == 'wc_main_On') {
            activeIds['handrail_type'] = '';
            activeIds['sidepanel_design'] = '';
            // activeIds['sidepanel_button'] = "";
            activeIds['Mirror_design'] = '';
            activeIds['handrail_position'] = '';
            activeIds['EBCover_side'] = '';
          } else {
            activeIds['wc_handrail_type'] = '';
            activeIds['wc_sidepanel_design'] = '';
            // activeIds['wc_sidepanel_button'] = "";
            activeIds['wc_Mirror_design'] = '';
            activeIds['wc_handrail_position'] = '';
            activeIds['wc_EBCover_side'] = '';
          }
        }

        // Remove RNdesign check in hall
        if (mode.component == 'Hall') {
          document.getElementsByName('Ind_RNPanel').forEach((elem) => {
            elem.checked = false;
          });
          delete activeIds['Ind_RNPanel'];
        }

        // Reset camera pos
        if (this.viewController) this.viewController.setCamPreset(5);
      }

      // Load stored set
      else if (preset == -1 && sessionStorage[mode.name]) {
        activeIds = JSON.parse(sessionStorage[mode.name]).Pattern;

        // For cage models
        if (mode.component == 'Cage') {
          if (activeIds['wc_main'] == 'wc_main_On') {
            activeIds['handrail_type'] = '';
            activeIds['sidepanel_design'] = '';
            // activeIds['sidepanel_button'] = "";
            activeIds['Mirror_design'] = '';
            activeIds['handrail_position'] = '';
            activeIds['EBCover_side'] = '';
          } else {
            activeIds['wc_handrail_type'] = '';
            activeIds['wc_sidepanel_design'] = '';
            // activeIds['wc_sidepanel_button'] = "";
            activeIds['wc_Mirror_design'] = '';
            activeIds['wc_handrail_position'] = '';
            activeIds['wc_EBCover_side'] = '';
          }

          if (activeIds['trunk'] === undefined) activeIds['trunk'] = '';
        } else {
          // Hall models
          if (activeIds['maku_color'] === undefined)
            activeIds['maku_color'] = '';
        }

        // Override door pattern
        if (/[Nn]ormal$/.test(sessionStorage['doorPattern'])) {
          if (mode.component == 'Cage')
            activeIds['door_design'] = 'door_design_Normal';
          else activeIds['door_glass'] = 'door_glass_normal';
        } else if (/[Ss]mall$/.test(sessionStorage['doorPattern'])) {
          if (mode.component == 'Cage')
            activeIds['door_design'] = 'door_design_WinSmall';
          else activeIds['door_glass'] = 'door_glass_small';
        } else {
          if (mode.component == 'Cage')
            activeIds['door_design'] = 'door_design_WinLarge';
          else activeIds['door_glass'] = 'door_glass_large';
        }
      } else if (preset == -1) {
        // Not stored yet
        activeIds = JSON.parse(
          JSON.stringify(this.Presets[mode.name].slice(-1)[0])
        );

        if (sessionStorage['doorPattern']) {
          // Override door pattern
          if (/[Nn]ormal$/.test(sessionStorage['doorPattern'])) {
            if (mode.component == 'Cage')
              activeIds['door_design'] = 'door_design_Normal';
            else activeIds['door_glass'] = 'door_glass_normal';
          } else if (/[Ss]mall$/.test(sessionStorage['doorPattern'])) {
            if (mode.component == 'Cage')
              activeIds['door_design'] = 'door_design_WinSmall';
            else activeIds['door_glass'] = 'door_glass_small';
          } else {
            if (mode.component == 'Cage')
              activeIds['door_design'] = 'door_design_WinLarge';
            else activeIds['door_glass'] = 'door_glass_large';
          }
        }

        // Remove RNdesign check in hall
        if (mode.component == 'Hall') {
          document.getElementsByName('Ind_RNPanel').forEach((elem) => {
            elem.checked = false;
          });
          delete activeIds['Ind_RNPanel'];
        } else {
          // For cage models
          if (activeIds['wc_main'] == 'wc_main_On') {
            activeIds['handrail_type'] = '';
            activeIds['sidepanel_design'] = '';
            // activeIds['sidepanel_button'] = "";
            activeIds['Mirror_design'] = '';
            activeIds['handrail_position'] = '';
            activeIds['EBCover_side'] = '';
          } else {
            activeIds['wc_handrail_type'] = '';
            activeIds['wc_sidepanel_design'] = '';
            // activeIds['wc_sidepanel_button'] = "";
            activeIds['wc_Mirror_design'] = '';
            activeIds['wc_handrail_position'] = '';
            activeIds['wc_EBCover_side'] = '';
          }
        }
      }
    }

    let hasDisabled = true;
    let disables = new Set();
    while (hasDisabled) {
      // Get disables
      disables = new Set();
      Object.keys(activeIds).forEach((key) => {
        if (activeIds[key] != null && activeIds[key] != '') {
          const c = this.getPatternsById(activeIds[key], modeJson);
          if (!c) console.warn('Object not found: ' + activeIds[key]);
          const disable = c.parts.patterns.filter(
            (pattern) => pattern.id == activeIds[key]
          )[0].disable;
          if (disable != null) {
            disable.forEach((di) => {
              disables.add(di);
            });
          }
        } else {
          // All disabled by null or no value
          const c = this.getPatternsByPartId(key, modeJson);
          if (!c) console.warn('Object not found: ' + key);
          c.parts.patterns.forEach((pattern) => {
            disables.add(pattern.id);
          });
        }
      });

      hasDisabled = false;
      // Find disabled in activeIds
      Object.keys(activeIds).forEach((key) => {
        if (disables.has(activeIds[key])) {
          // disabled
          hasDisabled = true;
          const c = this.getPatternsById(activeIds[key], modeJson);
          let isFound = false;
          for (let pattern of c.parts.patterns) {
            if (!disables.has(pattern.id)) {
              // Not in disables
              activeIds[key] = pattern.id;
              isFound = true;
              break;
            }
          }
          if (!isFound) {
            // All diabled
            activeIds[key] = '';
          }
        }
      });
    }

    // Update door signal state
    if (activeIds['doorsignal'] == 'doorsignal_On')
      this.animateParam.doorSignal.isEnabled = true;
    else this.animateParam.doorSignal.isEnabled = false;

    let filtered = new Object();

    // For cage optional buttons
    let hasAddComputed = false;
    await Promise.all(
      Object.keys(activeIds).map(async (key) => {
        let cat;
        if (activeIds[key] == null || activeIds[key] == '') {
          cat = this.getPatternsByPartId(key, modeJson);
        } else {
          cat = this.getPatternsById(activeIds[key], modeJson);
        }

        switch (cat.parts.type) {
          case 'Object': {
            if (cat.parts.filter != null) {
              // Filtered
              if (filtered[cat.parts.filter] == null) {
                // Initialize
                let obj = {
                  visible: new Set(),
                  hidden: new Set(),
                };
                filtered[cat.parts.filter] = obj;
              }

              // Filtering
              // for cage's optional buttons
              if (
                mode.component == 'Cage' &&
                (cat.parts.id == 'mainpanel_encho' ||
                  cat.parts.id == 'mainpanel_pet')
              ) {
                if (hasAddComputed) return;
                hasAddComputed = true;

                // active status
                let active = [];
                if (activeIds['mainpanel_encho'] == 'mainpanel_encho_On')
                  active.push('encho');
                if (activeIds['mainpanel_pet'] == 'mainpanel_pet_On')
                  active.push('pet');

                let buttons = [];
                buttons.push(this.scene.getObjectByName('22_MainPanel_Button'));
                if (this.scene.getObjectByName('27_SubPanelButton'))
                  buttons.push(this.scene.getObjectByName('27_SubPanelButton'));

                switch (active.length) {
                  case 0: {
                    this.scene
                      .getObjectByName('20_MainPanel')
                      .traverse((obj) => {
                        if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)Panel_.+_Add0$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].visible.add(obj.name);
                        else if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)Panel_.+_Add1_.+$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                        else if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)Panel_.+_Add2_.+$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                      });

                    buttons.forEach((b) => {
                      b.traverse((obj) => {
                        if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Ee]ncho$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                        else if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Pp]et_[Ss]ingle$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                        else if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Pp]et_[Tt]win$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                        else if (
                          /^98_Petbutton_Sticker_(Main|Sub)_(1|2)$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                      });
                    });
                    break;
                  }
                  case 1: {
                    this.scene
                      .getObjectByName('20_MainPanel')
                      .traverse((obj) => {
                        if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)Panel_.+_Add0$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                        else if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)Panel_.+_Add1_.+$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].visible.add(obj.name);
                        else if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)Panel_.+_Add2_.+$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                      });

                    if (active[0] == 'encho') {
                      buttons.forEach((b) => {
                        b.traverse((obj) => {
                          if (
                            /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Ee]ncho$/.test(
                              obj.name
                            )
                          )
                            filtered[cat.parts.filter].visible.add(obj.name);
                          else if (
                            /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Pp]et_[Ss]ingle$/.test(
                              obj.name
                            )
                          )
                            filtered[cat.parts.filter].hidden.add(obj.name);
                          else if (
                            /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Pp]et_[Tt]win$/.test(
                              obj.name
                            )
                          )
                            filtered[cat.parts.filter].hidden.add(obj.name);
                          else if (
                            /^98_Petbutton_Sticker_(Main|Sub)_(1|2)$/.test(
                              obj.name
                            )
                          )
                            filtered[cat.parts.filter].hidden.add(obj.name);
                        });
                      });
                    } else {
                      buttons.forEach((b) => {
                        b.traverse((obj) => {
                          if (
                            /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Ee]ncho$/.test(
                              obj.name
                            )
                          )
                            filtered[cat.parts.filter].hidden.add(obj.name);
                          else if (
                            /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Pp]et_[Ss]ingle$/.test(
                              obj.name
                            )
                          )
                            filtered[cat.parts.filter].visible.add(obj.name);
                          else if (
                            /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Pp]et_[Tt]win$/.test(
                              obj.name
                            )
                          )
                            filtered[cat.parts.filter].hidden.add(obj.name);
                          else if (
                            /^98_Petbutton_Sticker_(Main|Sub)_1$/.test(obj.name)
                          )
                            filtered[cat.parts.filter].visible.add(obj.name);
                          else if (
                            /^98_Petbutton_Sticker_(Main|Sub)_2$/.test(obj.name)
                          )
                            filtered[cat.parts.filter].hidden.add(obj.name);
                        });
                      });
                    }
                    break;
                  }
                  case 2: {
                    this.scene
                      .getObjectByName('20_MainPanel')
                      .traverse((obj) => {
                        if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)Panel_.+_Add0$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                        else if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)Panel_.+_Add1_.+$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                        else if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)Panel_.+_Add2_.+$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].visible.add(obj.name);
                      });

                    buttons.forEach((b) => {
                      b.traverse((obj) => {
                        if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Ee]ncho$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].visible.add(obj.name);
                        else if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Pp]et_[Ss]ingle$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                        else if (
                          /^([A-Z]+[0-9]+)_(Main|Sub)PanelButton_.+_[Pp]et_[Tt]win$/.test(
                            obj.name
                          )
                        )
                          filtered[cat.parts.filter].visible.add(obj.name);
                        else if (
                          /^98_Petbutton_Sticker_(Main|Sub)_1$/.test(obj.name)
                        )
                          filtered[cat.parts.filter].hidden.add(obj.name);
                        else if (
                          /^98_Petbutton_Sticker_(Main|Sub)_2$/.test(obj.name)
                        )
                          filtered[cat.parts.filter].visible.add(obj.name);
                      });
                    });
                    break;
                  }
                }
              } else {
                // anyother else
                if (
                  !cat.parts.patterns.filter(
                    (pattern) => pattern.id == activeIds[key]
                  ).length
                )
                  return;
                const comp = cat.parts.patterns.filter(
                  (pattern) => pattern.id == activeIds[key]
                )[0].components;
                if (Array.isArray(comp)) {
                  comp[0].split(',').forEach((c) => {
                    filtered[cat.parts.filter].visible.add(c);
                  });
                  if (comp.length == 2) {
                    comp[1].split(',').forEach((c) => {
                      filtered[cat.parts.filter].hidden.add(c);
                    });
                  }
                }
              }
            } else {
              // Single
              // to avoid conflicting
              const hasVisible = new Set();
              cat.parts.patterns.forEach((pattern) => {
                if (pattern.id == activeIds[key]) {
                  if (!Array.isArray(pattern.components)) return;

                  pattern.components[0].split(',').forEach((component) => {
                    const comp = this.scene.getObjectByName(component);
                    if (comp == null) {
                      // Exclude Cage's hall doors on Hall scene
                      if (
                        mode.component == 'Cage' &&
                        component.includes('HallDoor')
                      )
                        return;

                      console.warn('Object not found: ' + component);
                      return;
                    }
                    if (comp.type == 'Mesh') {
                      comp.visible = true;
                      hasVisible.add(comp);
                    } else {
                      comp.traverse((obj) => {
                        if (obj.type == 'Mesh') {
                          obj.visible = true;
                        }
                        hasVisible.add(obj);
                      });
                    }
                  });

                  // Set lightmap values
                  if (pattern.lightmap != null) {
                    this.lightMapState.default_prefix =
                      pattern.lightmap.default_prefix;
                    this.lightMapState.lightmap_targets =
                      pattern.lightmap.lightmap_targets;
                    this.lightMapState.target_prefix =
                      pattern.lightmap.target_prefix;
                  }
                } else {
                  if (!Array.isArray(pattern.components)) return;

                  pattern.components[0].split(',').forEach((component) => {
                    const comp = this.scene.getObjectByName(component);
                    if (comp == null) {
                      // Exclude Cage's hall doors on Hall scene
                      if (
                        mode.component == 'Cage' &&
                        component.includes('HallDoor')
                      )
                        return;

                      console.warn('Object not found: ' + component);
                      return;
                    }
                    if (hasVisible.has(comp)) return;
                    if (comp.type == 'Mesh') comp.visible = false;
                    else {
                      comp.traverse((obj) => {
                        if (hasVisible.has(obj)) return;
                        if (obj.type == 'Mesh') obj.visible = false;
                      });
                    }
                  });
                }
              });
            }
            break;
          }

          case 'Material': {
            if (
              !cat.parts.patterns.filter(
                (pattern) => pattern.id == activeIds[key]
              ).length
            )
              return;
            const pat = cat.parts.patterns.filter(
              (pattern) => pattern.id == activeIds[key]
            )[0];
            const mat = this.scene.getMaterialByName(pat.material_name);
            if (mat == null) {
              if (!/^floor_MD_(Off|On)/.test(pat.id))
                console.warn('Material not found: ' + pat.material_name);
              return;
            }

            // For cage indicator
            if (
              mode.component == 'Cage' &&
              pat.id == 'mainpanel_indicator_White'
            ) {
              this.animateParam.indicatorColor = 'White';
              await this.animator.setCageIndicator();
              break;
            } else if (
              mode.component == 'Cage' &&
              pat.id == 'mainpanel_indicator_Black'
            ) {
              this.animateParam.indicatorColor = 'Black';
              await this.animator.setCageIndicator();
              break;
            }

            // For hall indicator
            if (mode.component == 'Hall' && pat.id == 'ind_bg_White') {
              this.animateParam.indicatorColor = 'White';
              await this.animator.updateHallLCDIndicators();
              break;
            } else if (mode.component == 'Hall' && pat.id == 'ind_bg_Black') {
              this.animateParam.indicatorColor = 'Black';
              await this.animator.updateHallLCDIndicators();
              break;
            }

            pat.targets.forEach((target) => {
              const tgt = this.scene.getObjectByName(target);
              if (!tgt) {
                console.warn('Object not found:' + target);
                return;
              }
              if (tgt.material != null) tgt.material = mat;
              else {
                tgt.traverse((obj) => {
                  if (obj.material != null) obj.material = mat;
                });
              }
            });
            break;
          }

          case 'Lightmap': {
            if (
              !cat.parts.patterns.filter(
                (pattern) => pattern.id == activeIds[key]
              ).length
            )
              return;
            const pat = cat.parts.patterns.filter(
              (pattern) => pattern.id == activeIds[key]
            )[0];
            this.lightMapState.key = pat.key;
            break;
          }
          default:
            break;
        }
      })
    );

    // HallFrame
    if (
      mode.component == 'Hall' &&
      activeIds['Hall_Interphone'] == 'interphone_on' &&
      /^frame_design_(SS|LS)$/.test(activeIds['frame_design'])
    ) {
      if (filtered['hallframe_interphone'] == null) {
        // Initialize
        filtered['hallframe_interphone'] = {
          visible: new Set(),
          hidden: new Set(),
        };
      }

      if (/^frame_color_Deco/.test(activeIds['frame_color'])) {
        switch (mode.type) {
          case 'R9':
            filtered['hallframe_interphone'].visible = new Set([
              'R9_HallFrame_LS_InterphoneDeco',
              'R9_HallFrame_SS_InterphoneDeco',
              'R9_InterfoneDeco_Frame_Common',
              'R9_InterfoneDeco_Frame_Color',
            ]);
            filtered['hallframe_interphone'].hidden = new Set([
              'R9_HallFrame_LS_Interphone',
              'R9_HallFrame_SS_Interphone',
              'R9_Interfone_Frame_Common',
              'R9_Interfone_Frame_Color',
            ]);
            break;

          case 'B750':
            filtered['hallframe_interphone'].visible = new Set([
              'B750_HallFrame_SKowaku_InterphoneDeco',
              'B750_InterfoneDeco_Frame_Common',
              'B750_InterfoneDeco_Frame_Color',
            ]);
            filtered['hallframe_interphone'].hidden = new Set([
              'B750_HallFrame_SKowaku_InterphoneWall',
              'B750_Interfone_Frame_Common',
              'B750_Interfone_Frame_Color',
            ]);
            break;
        }
      } else {
        switch (mode.type) {
          case 'R9':
            filtered['hallframe_interphone'].hidden = new Set([
              'R9_HallFrame_LS_InterphoneDeco',
              'R9_HallFrame_SS_InterphoneDeco',
              'R9_InterfoneDeco_Frame_Common',
              'R9_InterfoneDeco_Frame_Color',
            ]);
            filtered['hallframe_interphone'].visible = new Set([
              'R9_HallFrame_LS_Interphone',
              'R9_HallFrame_SS_Interphone',
              'R9_Interfone_Frame_Common',
              'R9_Interfone_Frame_Color',
            ]);
            break;

          case 'B750':
            filtered['hallframe_interphone'].hidden = new Set([
              'B750_HallFrame_SKowaku_InterphoneDeco',
              'B750_InterfoneDeco_Frame_Common',
              'B750_InterfoneDeco_Frame_Color',
            ]);
            filtered['hallframe_interphone'].visible = new Set([
              'B750_HallFrame_SKowaku_Interphone',
              'B750_Interfone_Frame_Common',
              'B750_Interfone_Frame_Color',
            ]);
            break;
        }
      }
    }

    // Apply filtered result
    Object.keys(filtered).forEach((key) => {
      filtered[key].visible.forEach((c) => {
        const comp = this.scene.getObjectByName(c);
        if (comp == null) {
          console.warn('Object not found: ' + c);
          return;
        }
        if (comp.type == 'Mesh') comp.visible = true;
        else {
          comp.traverse((obj) => {
            if (obj.type == 'Mesh') obj.visible = true;
          });
        }
      });
      filtered[key].hidden.forEach((c) => {
        const comp = this.scene.getObjectByName(c);
        if (comp == null) {
          console.warn('Object not found: ' + c);
          return;
        }

        if (comp.type == 'Mesh') comp.visible = false;
        else {
          comp.traverse((obj) => {
            if (obj.type == 'Mesh') obj.visible = false;
          });
        }
      });
    });

    if (!modeJson) {
      // Reset disabled radio
      this.disableIds.forEach((di) => {
        document.getElementById(di).disabled = false;
      });

      // Apply disables
      disables.forEach((di) => {
        if (document.getElementById(di)) {
          document.getElementById(di).disabled = true;
          document.getElementById(di).checked = false;
        } else if (di) {
          console.warn(`Disable ID "${di}" is not found.`);
        }
      });
      this.disableIds = Array.from(disables);

      // Apply activeIds
      Object.keys(activeIds).forEach((key) => {
        if (activeIds[key] != null && activeIds[key] != '') {
          document.getElementById(activeIds[key]).checked = true;
        }
      });
    }

    if (this.loadedModel.component == 'Cage') {
      // Update Lightmap if values not null
      if (
        this.lightMapState.needsUpdate &&
        this.lightMapState.target_prefix &&
        this.lightMapState.key
      )
        await this.updateLightMap(
          [
            {
              path:
                'assets/models/' +
                this.loadedModel.type +
                '_Cage/Lightmap/' +
                this.lightMapState.target_prefix +
                this.lightMapState.key +
                '.exr',
              targets: [this.lightMapState.lightmap_targets],
            },
            {
              path:
                'assets/models/' +
                this.loadedModel.type +
                '_Cage/Lightmap/' +
                this.lightMapState.default_prefix +
                this.lightMapState.key +
                '.exr',
            },
          ],
          'Cage'
        );

      // Compare buttons
      if (
        /^(mainpanel_layout|mainpanel_button|mainpanel_button_position|mainpanel_encho|mainpanel_pet|subpanel|sidepanel_position)/.test(
          id
        )
      ) {
        this.animator.onCagePatternCompare(id);
      }

      // Toggle point light
      if (activeIds['ceiling_design'] == 'ceiling_design_DX02') {
        this.state.pointIntensity = 0;
        this.updateLights();
      } else {
        this.state.pointIntensity = 0.2;
        this.updateLights();
      }

      this.updateFloorUI();
    } else if (this.loadedModel.component == 'Hall') {
      // Update Lanterns
      if (/^ind_lantern_/.test(id)) {
        if (/_orange$/.test(id)) {
          this.animateParam.lanternColor = 'Orange';
        } else {
          this.animateParam.lanternColor = 'White';
        }

        this.animator.updateHallLanterns();
      }

      // Update Cage Lightmap
      if (this.lightMapState.needsUpdate && modeJson)
        await this.updateLightMap(
          [
            {
              path:
                'assets/models/' +
                this.loadedModel.type +
                '_Cage/Lightmap/' +
                this.lightMapState.target_prefix +
                this.lightMapState.key +
                '.exr',
              targets: [this.lightMapState.lightmap_targets],
            },
            {
              path:
                'assets/models/' +
                this.loadedModel.type +
                '_Cage/Lightmap/' +
                this.lightMapState.default_prefix +
                this.lightMapState.key +
                '.exr',
            },
          ],
          'Cage'
        );
    }

    console.debug(
      '[DEBUG] Pattern updated in %d ms: %s',
      (performance.now() - t0).toFixed(0),
      mode.component
    );
    this.storePattern();
    this.hideSpinner();
  }

  async updateLightMap(lightmaps, name, scene = this.scene) {
    let t0 = performance.now();

    let materials = getAllMaterials(scene.getObjectByName(name));

    await Promise.all(
      lightmaps.map(async (lightmap) => {
        let ext = lightmap.path.split('.').pop();
        let Yflag;
        let loader;
        switch (ext) {
          case 'png':
          case 'jpg':
            loader = new TextureLoader();
            Yflag = false;
            break;
          case 'exr':
            loader = new EXRLoader();
            Yflag = true;
            break;
          default:
            return;
        }

        const map = await loader.loadAsync(lightmap.path);
        map.name = lightmap.path;
        // targetsが指定されていた場合
        if (lightmap.targets != null) {
          let targetMeshs = [];
          lightmap.targets.forEach((target) => {
            let targetObj = scene.getObjectByName(target);
            if (!targetObj) console.warn('Object not found: ' + target);
            // targetがMeshの場合
            if (targetObj.type == 'Mesh') {
              targetMeshs.push(targetObj);
            } else {
              // targetがそれ以外の場合
              targetObj.traverse((object) => {
                if (object.type == 'Mesh') targetMeshs.push(object);
              });
            }
          });

          targetMeshs.forEach((mesh) => {
            if (!materials.has(mesh.material)) return;

            mesh.material.lightMap = map;
            mesh.material.lightMapIntensity = this.state.lightMapIntensity;
            mesh.material.lightMap.flipY = Yflag;
            mesh.material.aoMap = map;
            mesh.material.aoMapIntensity = this.state.aoMapIntensity;
            mesh.material.aoMap.flipY = Yflag;
            mesh.material.needsUpdate = true;

            // 反映済みマテリアルをキューから削除
            materials.delete(mesh.material);
          });
        } else {
          // targetsが指定されていない場合、既に指定済みのものを除く全てに適用
          materials.forEach((material) => {
            material.lightMap = map;
            material.lightMapIntensity = this.state.lightMapIntensity;
            material.lightMap.flipY = Yflag;
            material.aoMap = map;
            material.aoMapIntensity = this.state.aoMapIntensity;
            material.aoMap.flipY = Yflag;
            material.needsUpdate = true;
          });
        }
      })
    );

    this.lightMapState.needsUpdate = false;

    console.debug(
      '[DEBUG] LightMap updated in %d ms: %s',
      (performance.now() - t0).toFixed(0),
      name
    );

    // if (
    //   name == 'Cage' &&
    //   !this.generatedEnvMap &&
    //   this.loadedModel.component == 'Cage'
    // )
    //   this.updateEnvMap();
  }

  generateEnvMap() {
    console.debug('[DEBUG] Generating EnvMap...');
    let t0 = performance.now();

    (async () => {
      // Apply preset for render
      await this.updatePatterns(null, 0);

      setTimeout(async () => {
        this.showSpinner();

        // Store visible status
        const visibleStatus = [];
        for (const col of this.scene.getObjectByName('Cage').children) {
          if (col.name.startsWith('_Col')) {
            visibleStatus.push(col.visible);
            col.visible = true;
          }
        }

        // Load environment for render
        const environment = environments.filter(
          (entry) => entry.name === 'photo_studio_01_4k'
        )[0];
        const envTex = await this.getCubeMapTexture(environment);
        const defaultEnvMap = envTex.envMap;

        const targetMaterials = getAllMaterials(
          this.scene.getObjectByName('Cage')
        );

        // Apply default envMap
        // Material Correction
        targetMaterials.forEach((mat) => {
          switch (mat.name) {
            case 'M_Wall_AluminumSilver':
            case 'M_MainPanel_Text_AluminumSilver':
            case 'M_StainlessHairline':
            case 'M_MainPanel_Text_StainlessHairline':
            case 'M_StainlessVibration':
            case 'M_MainPanel_Text_StainlessVibration':
            case 'T_SidePanel_StainlessHairline':
            case 'T_SidePanel_StainlessVibration':
            case 'M_Ceiling_AluminumSilver':
            case 'M_Ceiling_Alumite':
              mat.metalness = 0.85;
            case 'M_Mirror': {
              mat.envMap = defaultEnvMap;
              mat.envMapIntensity = 50;
              mat.needsUpdate = true;
              break;
            }

            case 'M_Indicator_Arrow':
            case 'M_Indicator_Floor':
            case 'M_Indicator_Message':
            case 'M_Indicator_Encho_Arrow':
            case 'M_Indicator_Encho_Floor':
            case 'M_Indicator_Encho_Message':
            case 'M_Mon_Icon':
              mat.emissiveIntensity = 0;
            case 'M_Wall_CraftWood':
              mat.roughness = 1;
              mat.roughnessMap = null;
            default: {
              mat.envMap = defaultEnvMap;
              mat.needsUpdate = true;
              break;
            }
          }
        });

        // render
        let t1 = performance.now();
        const obj = this.scene.getObjectByName('Cage');
        this.cubeCamera.position.copy(obj.position);
        this.cubeCamera.update(this.renderer, this.scene);
        this.renderer.toneMappingExposure = this.state.toneMapExposure;

        let envMap = this.pmremGenerator.fromCubemap(
          this.cubeRenderTarget.texture
        ).texture;
        this.pmremGenerator.dispose();

        this.cubeCamera.rotation.y = Math.PI / 2;
        this.cubeCamera.update(this.renderer, this.scene);
        const equiUnmanaged = new CubemapToEquirectangular(
          this.renderer,
          false
        );
        equiUnmanaged.convert(this.cubeCamera);

        console.debug(
          '[DEBUG] EnvMap Rendered in %d ms',
          (performance.now() - t1).toFixed(0)
        );

        // apply
        targetMaterials.forEach((mat) => {
          mat.envMap = envMap;
          mat.envMapIntensity = 1;
          mat.needsUpdate = true;
        });

        // restore visible status
        let idx = 0;
        for (const col of this.scene.getObjectByName('Cage').children) {
          if (col.name.startsWith('_Col')) {
            col.visible = visibleStatus[idx];
            idx++;
          }
        }

        this.hideSpinner();

        console.debug(
          '[DEBUG] EnvMap generated in %f ms: %d materials',
          (performance.now() - t0).toFixed(0),
          targetMaterials.size
        );
      }, 0.25);
    })();
  }

  getPatternsById(id, json) {
    if (!json) json = this.jsonData;

    let cat = null;
    let breakFlag = false;
    for (let category of json) {
      for (let part of category.parts) {
        for (let pattern of part.patterns) {
          if (pattern.id == id) {
            let tempcat = JSON.parse(JSON.stringify(category));
            tempcat.parts = JSON.parse(JSON.stringify(part));
            cat = tempcat;
            breakFlag = true;
            break;
          }
          if (breakFlag) break;
        }
        if (breakFlag) break;
      }
      if (breakFlag) break;
    }
    return cat;
  }

  getPatternsByPartId(id, json) {
    if (!json) json = this.jsonData;

    let cat = null;
    let breakFlag = false;
    for (let category of json) {
      for (let part of category.parts) {
        if (part.id == id) {
          let tempcat = JSON.parse(JSON.stringify(category));
          tempcat.parts = JSON.parse(JSON.stringify(part));
          cat = tempcat;
          breakFlag = true;
          break;
        }
        if (breakFlag) break;
      }
      if (breakFlag) break;
    }
    return cat;
  }

  onControl() {
    if (!this.scene.getObjectByName('EV') || this.state.pause) return;
    // console.log(this.defaultCamera.position)
    // console.log(this.controls.target)

    // Setting collisions
    if (this.loadedModel.component == 'Cage') {
      if (this.raycaster.targets.length == 0) {
        this.raycaster.targets.push(this.scene.getObjectByName('_Col_Top'));
        this.raycaster.targets.push(this.scene.getObjectByName('_Col_Floor'));
        this.raycaster.targets.push(this.scene.getObjectByName('_Col_Front'));
        this.raycaster.targets.push(this.scene.getObjectByName('_Col_Back'));
        this.raycaster.targets.push(this.scene.getObjectByName('_Col_Left'));
        this.raycaster.targets.push(this.scene.getObjectByName('_Col_Right'));
      }
    }

    // Cast
    const hits = new Set();
    for (let x = -1; x <= 1; x += 0.02) {
      const c = new Vector2(x, 0);
      this.raycaster.setFromCamera(c, this.defaultCamera);
      const intersects = this.raycaster.intersectObjects(
        this.raycaster.targets,
        false
      );
      if (intersects.length != 0) {
        hits.add(intersects[0].object);
      }
    }
    for (let y = -1; y <= 1; y += 0.04) {
      const c = new Vector2(0, y);
      this.raycaster.setFromCamera(c, this.defaultCamera);
      const intersects = this.raycaster.intersectObjects(
        this.raycaster.targets,
        false
      );
      if (intersects.length != 0) {
        hits.add(intersects[0].object);
      }
    }

    // Result
    this.raycaster.targets.forEach((element) => {
      if (hits.has(element)) {
        element.visible = false;
        if (element.name == '_Col_Top') {
          const m = this.scene.getObjectByName('Mask_Ceiling');
          if (m) m.visible = false;
        } else if (element.name == '_Col_Front') {
          const m = this.scene.getObjectByName('Mask_Door');
          if (m) m.visible = false;
        }
      } else {
        element.visible = true;
        if (element.name == '_Col_Top') {
          const m = this.scene.getObjectByName('Mask_Ceiling');
          if (m) m.visible = true;
        } else if (element.name == '_Col_Front') {
          const m = this.scene.getObjectByName('Mask_Door');
          if (m) m.visible = true;
        }
      }
    });

    // Camera position based control
    if (this.loadedModel.component == 'Cage') {
      // On cage models
      // Hall
      if (this.defaultCamera.position.z >= 0.61) {
        const hall = this.scene.getObjectByName('Hall');
        if (hall != null) hall.visible = false;
        const hallWrap = this.scene.getObjectByName('HallWrap');
        if (hallWrap != null) hallWrap.visible = false;
      } else {
        const hall = this.scene.getObjectByName('Hall');
        if (hall != null) hall.visible = true;
        const hallWrap = this.scene.getObjectByName('HallWrap');
        if (hallWrap != null) hallWrap.visible = true;
      }
    }
  }

  async mouseCast(event) {
    if (
      !this.scene.getObjectByName('EV') ||
      this.state.pause ||
      !this.controls.enabled
    )
      return;

    let x = event.offsetX;
    let y = event.offsetY;

    let mouse = new Vector2();
    mouse.x =
      (x / document.getElementsByClassName('viewer')[0].clientWidth) * 2 - 1;
    mouse.y =
      -(y / document.getElementsByClassName('viewer')[0].clientHeight) * 2 + 1;

    let raycaster = new Raycaster();

    // Get targets
    let targets = new Set();

    // On cage model
    if (this.loadedModel.component == 'Cage') {
      if (this.scene.getObjectByName('_Col_Front').visible) {
        this.scene
          .getObjectByName('22_MainPanel_Button')
          .traverseVisible((obj) => {
            targets.add(obj);
          });

        const subbutton = this.scene.getObjectByName('27_SubPanelButton');
        if (subbutton) {
          subbutton.traverseVisible((obj) => {
            targets.add(obj);
          });
        }
      }
      if (this.scene.getObjectByName('_Col_Left').visible) {
        this.scene
          .getObjectByName('24_SidePanelButton_L')
          .traverseVisible((obj) => {
            targets.add(obj);
          });
      }
      if (this.scene.getObjectByName('_Col_Right').visible) {
        this.scene
          .getObjectByName('24_SidePanelButton_R')
          .traverseVisible((obj) => {
            targets.add(obj);
          });
      }
    }
    // On hall model
    else if (this.loadedModel.component == 'Hall') {
      this.scene.getObjectByName('66_HallButton').traverseVisible((obj) => {
        targets.add(obj);
      });
    }

    // Raycast
    raycaster.setFromCamera(mouse, this.defaultCamera);
    let intersects = raycaster.intersectObjects(Array.from(targets), false);
    // DEBUG
    // console.log(raycaster.intersectObject(this.scene));

    if (intersects.length >= 1) {
      // On cage model
      if (this.loadedModel.component == 'Cage') {
        await this.animator.onCageIntersect(intersects[0]);
      } else if (this.loadedModel.component == 'Hall') {
        await this.animator.onHallIntersect(intersects[0]);
      }
    }
  }

  reset() {
    this.updatePatterns();
    if (this.loadedModel.component == 'Cage') {
      this.defaultCamera.position.set(3.9, 0, -3.9);
      sessionStorage.removeItem('isEBCoverInit');
    }
  }

  progress(prog = null) {
    // Initialize
    if (this.percentage === undefined) {
      this.percentage = 0;
      this.progressTimer = performance.now();
      this.progressTimerCount = 0;

      document.querySelector('.cover_block').style.display = '';
      document.querySelector('.cover').style.backgroundColor =
        'rgba(33, 30, 32, 1)';
      document.querySelector('.cover').style.zIndex = '1';
    }

    // Set Progress from arg
    if (prog) {
      this.percentage = prog;
      this.progressTimer = performance.now();
      this.progressTimerCount = 0;
    }

    // Timer
    if (performance.now() - this.progressTimer > 1500) {
      if (this.progressTimerCount < 9) {
        this.percentage++;
        this.progressTimer = performance.now();
        this.progressTimerCount++;
      }
    }

    // Update HTML
    document
      .querySelector('.loader>div:nth-child(2)')
      .setAttribute('style', '--value:' + this.percentage);

    // Finish
    if (this.percentage == 100) {
      this.isLoaded = true;
      document.querySelector('.spinner').style.display = '';
      document.querySelector('.loader>div:nth-child(2)').style.display = 'none';
      document.querySelector('.loader>p').style.display = 'none';

      document.querySelector('.cover_block').remove();
      document.querySelector('.cover').style.backgroundColor = '';
      document.querySelector('.cover').style.zIndex = '';
    }
  }

  saveImage() {
    // Prepare canvas
    this.state.bgColor1 = '#FFFFFF';
    this.state.bgColor2 = '#FFFFFF';
    this.updateBackground();
    if (this.loadedModel.component == 'Cage') {
      if (this.loadedModel.type == 'B750') this.resize(null, 950, 800);
      else this.resize(null, 900, 700);
    } else this.resize(null, 900, 1250);
    const canvas = document.getElementsByTagName('canvas')[0];

    const currentCam = {
      Position: JSON.parse(JSON.stringify(this.defaultCamera.position)),
      Target: JSON.parse(JSON.stringify(this.controls.target)),
    };

    let images = [];

    // Cam1
    if (this.loadedModel.component == 'Cage' && this.loadedModel.type == 'B750')
      this.viewController.setCamPreset(7);
    else if (
      this.loadedModel.component == 'Cage' &&
      (this.loadedModel.type == 'EP26' || this.loadedModel.type == 'P24')
    )
      this.viewController.setCamPreset(9);
    else this.viewController.setCamPreset(5);
    this.render();

    const imagesrc1 = canvas.toDataURL('image/png');
    console.log(`x:${canvas.width} y:${canvas.height}`);
    const img1 = document.createElement('img');
    img1.src = imagesrc1;
    images.push(img1);

    if (this.loadedModel.component == 'Cage') {
      // Cam2
      if (this.loadedModel.type == 'B750') this.viewController.setCamPreset(8);
      else if (
        this.loadedModel.type == 'EP26' ||
        this.loadedModel.type == 'P24'
      )
        this.viewController.setCamPreset(10);
      else this.viewController.setCamPreset(6);
      this.render();

      console.log(`x:${canvas.width} y:${canvas.height}`)
      const imagesrc2 = canvas.toDataURL('image/png');
      const img2 = document.createElement('img');
      img2.src = imagesrc2;
      images.push(img2);
    }

    this.viewController.saveImage(images);

    // Restore camera
    if (this.loadedModel.component == 'Cage') {
      this.state.bgColor1 = '#000000';
      this.state.bgColor2 = '#000000';
    }

    this.updateBackground();
    this.defaultCamera.position.set(
      currentCam.Position.x,
      currentCam.Position.y,
      currentCam.Position.z
    );
    this.controls.target.set(
      currentCam.Target.x,
      currentCam.Target.y,
      currentCam.Target.z
    );
    this.controls.update();
  }

  storePattern() {
    // Get current selected value
    let activeIds = new Object();
    Array.from(
      document.getElementById('custom_ui').getElementsByTagName('input')
    ).forEach((input) => {
      if (input.type == 'radio' && input.checked) {
        activeIds[input.name] = input.value;
      }
    });

    if (!Object.keys(activeIds).length) return;

    const struct = {
      Pattern: activeIds,
    };
    sessionStorage[this.loadedModel.name] = JSON.stringify(struct);

    // Store door pattern
    if (this.loadedModel.component == 'Cage')
      sessionStorage['doorPattern'] = activeIds['door_design'];
    else sessionStorage['doorPattern'] = activeIds['door_glass'];
  }

  updateFloorUI(operation = null) {
    const checked = document.querySelector(
      `input[type='radio'][name='mainpanel_layout']:checked`
    );
    if (checked) {
      let floor = Number(checked.value.split('_').pop());
      if (operation == 'Add' && floor < 12) floor++;
      else if (operation == 'Sub' && floor > 2) floor--;

      document
        .querySelectorAll(`input[type='radio'][name='mainpanel_layout']`)
        .forEach((radio) => {
          radio.checked = false;
          radio.parentNode.style.display = 'none';
          if (Number(radio.value.split('_').pop()) == floor) {
            radio.checked = true;
            radio.parentNode.style.display = 'flex';
          }
        });

      if (floor >= 12)
        document.querySelectorAll('.UIArrow')[1].classList.add('disable');
      else if (floor <= 2)
        document.querySelectorAll('.UIArrow')[0].classList.add('disable');
      else
        document
          .querySelectorAll('.UIArrow')
          .forEach((arrow) => arrow.classList.remove('disable'));

      if (operation) window.updatePatterns(`mainpanel_layout_${floor}`);
    }
  }

  showSpinner() {
    this.state.pause = true;
    this.spinnerEl.style.display = '';
    this.coverEl.classList.add('show');
  }

  hideSpinner() {
    this.state.pause = false;
    this.spinnerEl.style.display = 'none';
    this.coverEl.classList.remove('show');
  }
}

function getAllMaterials(object) {
  const materials = new Set();
  object.traverse((node) => {
    if (node.material) materials.add(node.material);
  });
  return materials;
}

Scene.prototype.getMaterialByName = function (name) {
  const materials = getAllMaterials(this);
  let material = null;

  for (let mat of Array.from(materials)) {
    if (mat.name == name) {
      material = mat;
      break;
    }
  }
  return material;
};

function traverseMaterials(object, callback) {
  object.traverse((node) => {
    if (!node.isMesh) return;
    const materials = Array.isArray(node.material)
      ? node.material
      : [node.material];
    materials.forEach(callback);
  });
}

function calcWidth() {
  // Set pattern width
  document.querySelectorAll('.part_wrap').forEach((el) => {
    el.style.width = '0';
  });

  const catEl = document.querySelector('.category');
  if (!catEl) return;

  const pwidth = Math.floor((catEl.offsetWidth - 5 * 5 - 1) / 6);
  document.querySelectorAll('.part_wrap').forEach((el) => {
    el.style.width = '';
  });

  document.querySelectorAll('.pattern').forEach((pat) => {
    pat.style.width = pwidth + 'px';
  });

  // Set part width
  document.querySelectorAll('.part_wrap').forEach((partw) => {
    let len = partw.querySelector('.part').children.length;
    if (len > 6) len = 6;

    const imgEl = partw.querySelector('.part_wrap>img');
    if (imgEl) imgEl.style.width = pwidth * len + 5 * (len - 1) + 'px';
    const partEl = partw.querySelector('.part_wrap>.part');
    if (partEl) {
      // ind_lantern
      if (
        partw.querySelector('img')?.getAttribute('src') ==
        'assets/images/00_navigation/nav_cus_sec46.png'
      ) {
        len = 5;
      }
      // floor
      if (
        partw.querySelector('img')?.getAttribute('src') ==
        'assets/images/00_navigation/nav_cus_sec10.png'
      ) {
        len = 5;
      }

      partEl.style.width = pwidth * len + 5 * (len - 1) + 'px';
    }
  });

  // fukidashi
  document.querySelectorAll('.fukidashi').forEach((el) => {
    el.style.height = pwidth + 'px';
  });

  // label
  document.querySelectorAll('.pattern>label').forEach((pat) => {
    // Width override
    if (pat.getAttribute('for') == 'monitor_silver') {
      pat.style.width = pwidth * 1.64 + 'px';
      pat.parentElement.style.width = pwidth * 1.64 + 'px';
      pat.parentElement.parentElement.style.width = pwidth * 4 + 15 + 'px';
      pat.parentElement.parentElement.previousElementSibling.style.width =
        pwidth * 4 + 15 + 'px';
    } else pat.style.width = pwidth + 'px';
    pat.style.height = pat.querySelector('img').clientHeight + 'px';
  });

  // Floor Arrow UI
  document.querySelectorAll('.UIArrow').forEach((arrow) => {
    arrow.style.height = arrow.parentNode.style.height =
      document.querySelector('.pattern>label>img').clientHeight + 'px';
  });
}

// https://stackoverflow.com/a/9039885/1314762
function isIOS() {
  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
}

function getBaseDir(path) {
  var result = path.replace(/\\/g, '/').replace(/\/[^/]*$/, '');
  return result.match(/^[^/]*\.[^/\.]*$/) ? '' : result;
}
